import * as i2 from '@activia/ngx-components';
import { CoreModule, ButtonModule, cssVariableToThemeProperty, ThemeType, IconModule } from '@activia/ngx-components';
import * as i1 from '@angular/common';
import { CommonModule, PercentPipe, DatePipe, DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, forwardRef, Directive, Input, NgModule, Component, ChangeDetectionStrategy, ViewChild, ContentChildren, EventEmitter, Output, HostBinding, ElementRef } from '@angular/core';
import * as HighCharts from 'highcharts';
import { ReplaySubject, Subject, BehaviorSubject, combineLatest, filter as filter$1, animationFrameScheduler, timer, fromEvent, merge } from 'rxjs';
import { v4 } from 'uuid';
import { partial, isEqual } from 'lodash';
import more from 'highcharts/highcharts-more.src';
import solidgauge from 'highcharts/modules/solid-gauge.src';
import sunburst from 'highcharts/modules/sunburst.src';
import { filter, takeUntil, debounceTime, map, startWith, observeOn, endWith, withLatestFrom, skip, bufferCount, repeat } from 'rxjs/operators';
import 'leader-line';
import * as i1$1 from '@angular/cdk/overlay';
import ResizeObserver from 'resize-observer-polyfill';

const HIGHCHARTS_MODULES = new InjectionToken('HighchartsModules');
/**
 * this service maintains the global chart lib and sets common configuration of all charts
 */
class ChartLibService {
    constructor(_chartModules) {
        this._chartModules = _chartModules;
    }
    initModules() {
        this._chartModules.forEach(chartModule => {
            chartModule(HighCharts);
        });
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ChartLibService, deps: [{ token: HIGHCHARTS_MODULES }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ChartLibService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ChartLibService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [HIGHCHARTS_MODULES]
                }] }]; } });

class ChartThemeAdapterService {
    constructor(themeService) {
        this.themeService = themeService;
        /**
         * Theme MEGA F#@#!! HUGE OBJECT for highcharts
         */
        this._theme = {
            // colors: ['#2b908f', '#90ee7e', '#f45b5b', '#7798BF', '#aaeeee', '#ff0066', '#eeaaee', '#55BF3B', '#DF5353', '#7798BF', '#aaeeee'],
            chart: {
                backgroundColor: {
                    linearGradient: { x1: 0, y1: 0, x2: 1, y2: 1 },
                    stops: [
                        [0, '#2a2a2b'],
                        [1, '#3e3e40'],
                    ],
                },
                style: { fontFamily: 'var(--font-family)' },
                plotBorderColor: '#606063',
            },
            title: { style: { color: '#E0E0E3', textTransform: 'uppercase' } },
            subtitle: { style: { color: '#E0E0E3', textTransform: 'uppercase' } },
            xAxis: {
                gridLineColor: '#707073',
                labels: { style: { color: '#E0E0E3' } },
                lineColor: '#707073',
                minorGridLineColor: '#505053',
                tickColor: '#707073',
                title: { style: { color: '#A0A0A3' } },
            },
            yAxis: {
                gridLineColor: '#707073',
                labels: { style: { color: '#E0E0E3' } },
                lineColor: '#707073',
                minorGridLineColor: '#505053',
                tickColor: '#707073',
                tickWidth: 1,
                title: { style: { color: '#A0A0A3' } },
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                style: { color: '#F0F0F0' },
            },
            plotOptions: {
                series: {
                    dataLabels: { color: '#F0F0F3', style: { fontSize: '15px' } },
                    marker: { lineColor: '#333' },
                },
                boxplot: { fillColor: '#505053' },
                errorbar: { color: 'white' },
            },
            legend: {
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                itemStyle: { color: '#E0E0E3' },
                itemHoverStyle: { color: '#FFF' },
                itemHiddenStyle: { color: '#606063' },
                title: { style: { color: '#C0C0C0' } },
            },
            credits: { style: { display: 'none' } },
            drilldown: {
                activeAxisLabelStyle: { color: '#F0F0F3' },
                activeDataLabelStyle: { color: '#F0F0F3' },
            },
            navigation: {
                buttonOptions: { theme: { fill: '#505053', stroke: '#DDDDDD' } },
            },
            // scroll charts
            rangeSelector: {
                buttonTheme: {
                    fill: '#505053',
                    stroke: '#000000',
                    style: { color: '#CCC' },
                    states: {
                        hover: {
                            fill: '#707073',
                            stroke: '#000000',
                            style: { color: 'white' },
                        },
                        select: {
                            fill: '#000003',
                            stroke: '#000000',
                            style: { color: 'white' },
                        },
                    },
                },
                inputBoxBorderColor: '#505053',
                inputStyle: { backgroundColor: '#333', color: 'silver' },
                labelStyle: { color: 'silver' },
            },
            navigator: {
                handles: { backgroundColor: '#666', borderColor: '#AAA' },
                outlineColor: '#CCC',
                maskFill: 'rgba(255,255,255,0.1)',
                series: { color: '#7798BF', lineColor: '#A6C7ED' },
                xAxis: { gridLineColor: '#505053' },
            },
            scrollbar: {
                barBackgroundColor: '#808083',
                barBorderColor: '#808083',
                buttonArrowColor: '#CCC',
                buttonBackgroundColor: '#606063',
                buttonBorderColor: '#606063',
                rifleColor: '#FFF',
                trackBackgroundColor: '#404043',
                trackBorderColor: '#404043',
            },
        };
        /*** @ignore */
        this.chartsThemeSubject = new ReplaySubject(1);
        /*** @ignore */
        this._serviceDestroyed$ = new Subject();
        this.themeService.theme$.subscribe((theme) => {
            const chartStyles = {
                chart: {
                    backgroundColor: 'transparent',
                    style: { fontFamily: 'var(--font-family-headers)' },
                    plotBorderColor: theme.mainVariantBackground,
                },
                title: {
                    style: {
                        color: theme.mainOn,
                        textTransform: 'uppercase',
                        fontSize: '20px',
                    },
                },
                subtitle: {
                    style: { color: theme.mainVariantOn, textTransform: 'uppercase' },
                },
                xAxis: {
                    gridLineColor: theme.mainOn,
                    labels: { style: { color: theme.mainVariantOn } },
                    lineColor: theme.mainOn,
                    minorGridLineColor: theme.mainVariantOn,
                    tickColor: theme.mainOn,
                    title: { style: { color: theme.mainOn } },
                },
                yAxis: {
                    gridLineColor: theme.mainOn,
                    labels: { style: { color: theme.mainVariantOn } },
                    lineColor: theme.mainOn,
                    minorGridLineColor: theme.mainVariantOn,
                    tickColor: theme.mainOn,
                    tickWidth: 1,
                    title: { style: { color: theme.mainOn, fontFamily: 'var(--font-family)' } },
                },
                tooltip: {
                    backgroundColor: 'rgba(0, 0, 0, 0.85)',
                    style: { color: '#F0F0F0' },
                },
                // tooltip: {backgroundColor: theme.primaryBase, borderRadius: 2, style: {color: theme.primaryOn, opacity: 0.8}},
                plotOptions: {
                    series: {
                        dataLabels: {
                            color: '#F0F0F3',
                            style: { fontSize: '15px', fontFamily: 'var(--font-family)' },
                        },
                        marker: { lineColor: '#333' },
                    },
                    boxplot: { fillColor: '#505053' },
                    errorbar: { color: 'white' },
                },
                legend: {
                    backgroundColor: 'transparent',
                    itemStyle: { color: theme.mainOn },
                    itemHoverStyle: { color: theme.mainVariantOn },
                    itemHiddenStyle: { color: '#606063' },
                    title: { style: { color: theme.mainOn } },
                },
                credits: { style: { display: 'none' } },
                drilldown: {
                    activeAxisLabelStyle: { color: theme.mainOn },
                    activeDataLabelStyle: { color: theme.mainVariantOn },
                },
                navigation: {
                    buttonOptions: {
                        theme: { fill: '#505053', stroke: '#DDDDDD' },
                    },
                },
                rangeSelector: {
                    buttonTheme: {
                        fill: '#505053',
                        stroke: '#000000',
                        style: { color: '#CCC' },
                        states: {
                            hover: {
                                fill: '#707073',
                                stroke: '#000000',
                                style: { color: 'white' },
                            },
                            select: {
                                fill: '#000003',
                                stroke: '#000000',
                                style: { color: 'white' },
                            },
                        },
                    },
                    inputBoxBorderColor: theme.primaryVariant,
                    inputStyle: {
                        backgroundColor: theme.primaryVariant,
                        color: theme.primaryVariantOn,
                    },
                    labelStyle: { color: theme.primaryVariantOn },
                },
                navigator: {
                    handles: { backgroundColor: '#666', borderColor: '#AAA' },
                    outlineColor: '#CCC',
                    maskFill: 'rgba(255,255,255,0.1)',
                    series: { color: '#7798BF', lineColor: '#A6C7ED' },
                    xAxis: { gridLineColor: '#505053' },
                },
                scrollbar: {
                    //  huh ? todo
                    barBackgroundColor: theme.scrollbarTrack,
                    barBorderColor: theme.scrollbarTrack,
                    buttonArrowColor: theme.primaryOn,
                    buttonBackgroundColor: theme.primaryBase,
                    buttonBorderColor: theme.primaryBase,
                    rifleColor: '#FFF',
                    trackBackgroundColor: theme.scrollbarTrack,
                    trackBorderColor: theme.scrollbarTrack,
                },
                /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                // @ts-ignore
                // add the current theme to the the global chart option, that will allow us to access it from everywhere (mixins etc..)
                custom: theme,
            };
            // sets the global object
            HighCharts.setOptions(chartStyles);
            // todo convert to a palette with more colors
            // i.e extrapolate primary on a monochromatic palette
            // series should use the palette based on the order unless they request a particular theme palette
            this.chartsThemeSubject.next(chartStyles);
        });
    }
    /**
     * @ignore
     */
    ngOnDestroy() {
        this._serviceDestroyed$.next();
        this._serviceDestroyed$.complete();
    }
    /** an observable of highcharts options for theming */
    get chartTheme$() {
        return this.chartsThemeSubject.asObservable();
    }
    /**
     * this needs to be changed to another type.
     * we need to be able to get palettes and others
     */
    get chartThemeColors$() {
        return this.themeService.theme$;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ChartThemeAdapterService, deps: [{ token: i2.ThemeService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ChartThemeAdapterService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ChartThemeAdapterService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i2.ThemeService }]; } });

/**
 * mixin for all series type
 */
const mixinCartesianSeries = (Base) => {
    class SeriesMixin extends Base {
        get key() {
            if (!this._key) {
                this._key = v4();
            }
            return this._key;
        }
        get data() {
            return this._data;
        }
        set data(value) {
            this._data = value;
        }
        get name() {
            return this._name;
        }
        set name(value) {
            this._name = value;
        }
        get color() {
            return this._color;
        }
        set color(value) {
            this._color = value;
        }
        get step() {
            return this._step;
        }
        set step(value) {
            this._step = value;
        }
        get type() {
            return this._type;
        }
        set type(value) {
            this._type = value;
        }
        get maxDataItems() {
            return this._maxDataItems;
        }
        set maxDataItems(value) {
            this._maxDataItems = value;
        }
        get lineWidth() {
            return this._lineWidth;
        }
        set lineWidth(value) {
            this._lineWidth = value;
        }
        get areaFillOpacity() {
            return this._areaFillOpacity;
        }
        set areaFillOpacity(value) {
            this._areaFillOpacity = value;
        }
        get seriesChanged$() {
            return this._seriesChangedSubject.asObservable();
        }
        onDataChange({ data, name, color, type, step, maxDataItems, lineWidth, areaFillOpacity }) {
            if (data || name || color || type || step || maxDataItems || lineWidth || areaFillOpacity) {
                this._seriesChangedSubject.next({
                    ...(data ? { data: data.currentValue } : {}),
                    ...(name ? { name: name.currentValue } : {}),
                    ...(color ? { color: color.currentValue } : {}),
                    ...(type ? { color: color.currentValue } : {}),
                    ...(step ? { color: color.currentValue } : {}),
                    ...(maxDataItems ? { maxDataItems: maxDataItems.currentValue } : {}),
                    ...(lineWidth ? { lineWidth: lineWidth.currentValue } : {}),
                    ...(areaFillOpacity ? { fillOpacity: areaFillOpacity.currentValue } : {}),
                });
            }
        }
        constructor(...args) {
            super(...args);
            //#endregion
            this._seriesChangedSubject = new Subject();
        }
    }
    return SeriesMixin;
};
class CartesianSeries {
    constructor() { }
}
const _CartesianSeriesBase = mixinCartesianSeries(CartesianSeries);
/**
 * @ignore
 * base class in content  for series abstraction
 */
class CartesianSeriesDirective extends _CartesianSeriesBase {
}

var SeriesTypes;
(function (SeriesTypes) {
    // spline
    SeriesTypes["SPLINE"] = "spline";
    SeriesTypes["AREA_SPLINE"] = "area-spline";
    SeriesTypes["AREA_RANGE_SPLINE"] = "area-range-spline";
    // line
    SeriesTypes["LINE"] = "line";
    SeriesTypes["AREA_LINE"] = "area-line";
    SeriesTypes["AREA_RANGE_LINE"] = "area-range-line";
    // step line (no range for step line - looks weird)
    SeriesTypes["STEP_LINE"] = "step-line";
    SeriesTypes["AREA_STEP_LINE"] = "area-step-line";
})(SeriesTypes || (SeriesTypes = {}));

class LineSeriesDirective extends _CartesianSeriesBase {
    constructor() {
        super();
        this.type = SeriesTypes.LINE;
    }
    // changes are in the concrete directive because we want to ensure that data is parsed.
    ngOnChanges(simpleChanges) {
        this.onDataChange(simpleChanges);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: LineSeriesDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.1", type: LineSeriesDirective, selector: "avn-line-series", inputs: { data: "data", name: "name", color: "color", maxDataItems: "maxDataItems", lineWidth: "lineWidth", areaFillOpacity: "areaFillOpacity", type: "type" }, providers: [
            {
                provide: CartesianSeriesDirective,
                useExisting: forwardRef((() => LineSeriesDirective)),
            },
        ], usesInheritance: true, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: LineSeriesDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'avn-line-series',
                    providers: [
                        {
                            provide: CartesianSeriesDirective,
                            useExisting: forwardRef((() => LineSeriesDirective)),
                        },
                    ],
                    inputs: ['data', 'name', 'color', 'maxDataItems', 'lineWidth', 'areaFillOpacity'],
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { type: [{
                type: Input
            }] } });

class SplineSeriesDirective extends _CartesianSeriesBase {
    constructor() {
        super();
        this.type = SeriesTypes.SPLINE;
    }
    ngOnChanges(simpleChanges) {
        this.onDataChange(simpleChanges);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SplineSeriesDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.1", type: SplineSeriesDirective, selector: "avn-spline-series", inputs: { data: "data", name: "name", color: "color", maxDataItems: "maxDataItems", lineWidth: "lineWidth", areaFillOpacity: "areaFillOpacity", type: "type" }, providers: [
            {
                provide: CartesianSeriesDirective,
                useExisting: forwardRef((() => SplineSeriesDirective)),
            },
        ], usesInheritance: true, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SplineSeriesDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'avn-spline-series',
                    providers: [
                        {
                            provide: CartesianSeriesDirective,
                            useExisting: forwardRef((() => SplineSeriesDirective)),
                        },
                    ],
                    inputs: ['data', 'name', 'color', 'maxDataItems', 'lineWidth', 'areaFillOpacity'],
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { type: [{
                type: Input
            }] } });

class StepLineSeriesDirective extends _CartesianSeriesBase {
    constructor() {
        super();
        this.type = SeriesTypes.STEP_LINE;
    }
    // changes are in the concrete directive because we want to ensure that data is parsed.
    ngOnChanges(simpleChanges) {
        this.onDataChange(simpleChanges);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: StepLineSeriesDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.1", type: StepLineSeriesDirective, selector: "avn-step-line-series", inputs: { data: "data", name: "name", color: "color", maxDataItems: "maxDataItems", lineWidth: "lineWidth", areaFillOpacity: "areaFillOpacity", type: "type" }, providers: [
            {
                provide: CartesianSeriesDirective,
                useExisting: forwardRef((() => StepLineSeriesDirective)),
            },
        ], usesInheritance: true, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: StepLineSeriesDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'avn-step-line-series',
                    providers: [
                        {
                            provide: CartesianSeriesDirective,
                            useExisting: forwardRef((() => StepLineSeriesDirective)),
                        },
                    ],
                    inputs: ['data', 'name', 'color', 'maxDataItems', 'lineWidth', 'areaFillOpacity'],
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { type: [{
                type: Input
            }] } });

class AreaRangeSeriesDirective extends _CartesianSeriesBase {
    constructor() {
        super();
        this.type = SeriesTypes.AREA_RANGE_SPLINE;
    }
    // changes are in the concrete directive because we want to ensure that data is parsed.
    ngOnChanges(simpleChanges) {
        this.onDataChange(simpleChanges);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: AreaRangeSeriesDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.1", type: AreaRangeSeriesDirective, selector: "avn-area-range-series", inputs: { data: "data", name: "name", color: "color", maxDataItems: "maxDataItems", lineWidth: "lineWidth", areaFillOpacity: "areaFillOpacity", type: "type" }, providers: [
            {
                provide: CartesianSeriesDirective,
                useExisting: forwardRef((() => AreaRangeSeriesDirective)),
            },
        ], usesInheritance: true, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: AreaRangeSeriesDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'avn-area-range-series',
                    providers: [
                        {
                            provide: CartesianSeriesDirective,
                            useExisting: forwardRef((() => AreaRangeSeriesDirective)),
                        },
                    ],
                    inputs: ['data', 'name', 'color', 'maxDataItems', 'lineWidth', 'areaFillOpacity'],
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { type: [{
                type: Input
            }] } });

const mixinTooltipFormatter = (Base) => {
    class ChartTooltipFormatterMixin extends Base {
        constructor() {
            super(...arguments);
            this._precision = 0;
            this._labelvalueProp = '';
            this._uom = '';
            this._animation = true;
            this._shared = false;
            this._xDateFormat = undefined;
            this._headerFormat = undefined;
            this._pointFormat = undefined;
            this._footerFormat = '';
            this._tooltipChangedSubject = new ReplaySubject();
        }
        get precision() {
            return this._precision;
        }
        set precision(value) {
            if (this._precision !== value) {
                this._precision = value;
                this.onTooltipChange({ precision: value });
            }
        }
        get labelvalueProp() {
            return this._labelvalueProp;
        }
        set labelvalueProp(value) {
            if (this._labelvalueProp !== value) {
                this._labelvalueProp = value;
                this.onTooltipChange({ labelvalueProp: value });
            }
        }
        get uom() {
            return this._uom;
        }
        set uom(value) {
            if (this._uom !== value) {
                this._uom = value;
                this.onTooltipChange({ uom: value });
            }
        }
        get animation() {
            return this._animation;
        }
        set animation(value) {
            if (this._animation !== value) {
                this._animation = value;
                this.onTooltipChange({ animation: value });
            }
        }
        get shared() {
            return this._shared;
        }
        set shared(value) {
            if (this._shared !== value) {
                this._shared = value;
                this.onTooltipChange({ shared: true });
            }
        }
        get xDateFormat() {
            return this._xDateFormat;
        }
        set xDateFormat(value) {
            if (this._xDateFormat !== value) {
                this._xDateFormat = value;
                this.onTooltipChange({ xDateFormat: value });
            }
        }
        get headerFormat() {
            return this._headerFormat;
        }
        set headerFormat(value) {
            if (this._headerFormat !== value) {
                this._headerFormat = value;
                this.onTooltipChange({ headerFormat: value });
            }
        }
        get pointFormat() {
            return this._pointFormat;
        }
        set pointFormat(value) {
            if (this._pointFormat !== value) {
                this._pointFormat = value;
                this.onTooltipChange({ pointFormat: value });
            }
        }
        get footerFormat() {
            return this._footerFormat;
        }
        set footerFormat(value) {
            if (this._footerFormat !== value) {
                this._footerFormat = value;
                this.onTooltipChange({ footerFormat: value });
            }
        }
        get formatTooltipItem() {
            return this._formatTooltipItem;
        }
        set formatTooltipItem(value) {
            if (this._formatTooltipItem !== value) {
                this._formatTooltipItem = value;
                this.onTooltipChange({ formatTooltipItem: value });
            }
        }
        get customValueFormatter() {
            return this._customValueFormatter;
        }
        set customValueFormatter(value) {
            if (this._customValueFormatter !== value) {
                this._customValueFormatter = value;
                this.onTooltipChange({ customValueFormatter: value });
            }
        }
        get tooltipChanged$() {
            // perf if needed + scheduler or pipe in a debounce with all props
            return this._tooltipChangedSubject.asObservable();
        }
        onTooltipChange(tooltipChange) {
            this._tooltipChangedSubject.next(tooltipChange);
        }
    }
    return ChartTooltipFormatterMixin;
};
class TooltipFormatter {
    constructor() { }
}
const _ChartTooltipBase = mixinTooltipFormatter(TooltipFormatter);
class ChartTooltipDirective extends _ChartTooltipBase {
}

class SliceChartTooltipFormatterDirective extends _ChartTooltipBase {
    constructor() {
        super();
    }
    ngOnInit() {
        this.formatTooltipItem = partial(this.formatToolTip, this.customValueFormatter);
    }
    ngOnChanges({ labelValueType, customValueFormatter }) {
        if (labelValueType) {
            /** supports percent or the actual value */
            this.labelvalueProp = labelValueType.currentValue === 'percent' ? 'percentage' : 'y';
        }
        if (customValueFormatter?.currentValue != null && !customValueFormatter.firstChange) {
            this.formatTooltipItem = partial(this.formatToolTip, this.customValueFormatter);
        }
    }
    formatToolTip(customValueFormatter, tooltip) {
        // we use a formatter here since highchart tooltip.update won't work
        // as well, this ensures we can extend the model to use template refs.
        // todo add a template function and envoke it here
        const self = tooltip.options.self;
        // highcharts calls this with call, or bind so this is Highcharts.TooltipFormatterContextObject
        const tooltipVals = this;
        let tooltipValue = tooltipVals[self.tooltipFormat.labelvalueProp];
        let uom = tooltip.options.valueSuffix;
        if (customValueFormatter) {
            const formattedValue = customValueFormatter(tooltipValue);
            uom = formattedValue.unit;
            tooltipValue = formattedValue.value;
        }
        // eslint-disable-next-line max-len
        return `<span style="font-size: 24px; padding-top: 10px; margin-bottom: 10px">${tooltipValue.toFixed(tooltip.options.valueDecimals)} ${uom}</span><br><b>${tooltipVals.key}</b>`;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SliceChartTooltipFormatterDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.1", type: SliceChartTooltipFormatterDirective, selector: "avn-slice-chart-tooltip-formatter", inputs: { precision: "precision", uom: "uom", customValueFormatter: "customValueFormatter", labelValueType: "labelValueType" }, providers: [
            {
                provide: ChartTooltipDirective,
                useExisting: forwardRef((() => SliceChartTooltipFormatterDirective)),
            },
        ], usesInheritance: true, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SliceChartTooltipFormatterDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'avn-slice-chart-tooltip-formatter',
                    providers: [
                        {
                            provide: ChartTooltipDirective,
                            useExisting: forwardRef((() => SliceChartTooltipFormatterDirective)),
                        },
                    ],
                    inputs: ['precision', 'uom', 'customValueFormatter'],
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { labelValueType: [{
                type: Input
            }] } });

const mixinLegendFormatter = (Base) => {
    class ChartLegendFormatterMixin extends Base {
        constructor() {
            super(...arguments);
            this._precision = 0;
            this._uom = '';
            this._layout = 'horizontal';
            this._align = 'right';
            this._verticalAlign = 'middle';
            this._customValueFormatter = null;
            this._legendItemFormatter = null;
            this._legendChangedSubject = new ReplaySubject();
        }
        get precision() {
            return this._precision;
        }
        set precision(value) {
            if (this._precision !== value) {
                this._precision = value;
                this.onLegendChange({ precision: value });
            }
        }
        get uom() {
            return this._uom;
        }
        set uom(value) {
            if (this._uom !== value) {
                this._uom = value;
                this.onLegendChange({ uom: value });
            }
        }
        get layout() {
            return this._layout;
        }
        set layout(value) {
            if (this._layout !== value) {
                this._layout = value;
                this.onLegendChange({ layout: value });
            }
        }
        get align() {
            return this._align;
        }
        set align(value) {
            if (this._align !== value) {
                this._align = value;
                this.onLegendChange({ align: value });
            }
        }
        get verticalAlign() {
            return this._verticalAlign;
        }
        set verticalAlign(value) {
            if (this._verticalAlign !== value) {
                this._verticalAlign = value;
                this.onLegendChange({ verticalAlign: value });
            }
        }
        get customValueFormatter() {
            return this._customValueFormatter;
        }
        set customValueFormatter(value) {
            if (value && this._customValueFormatter !== value) {
                this._customValueFormatter = value;
            }
            else {
                this._customValueFormatter = null;
            }
            this.onLegendChange({ customValueFormatter: this._customValueFormatter });
        }
        get legendItemFormatter() {
            return this._legendItemFormatter;
        }
        set legendItemFormatter(value) {
            if (value && this._legendItemFormatter !== value) {
                this._legendItemFormatter = value;
            }
            else {
                this._legendItemFormatter = null;
            }
            this.onLegendChange({ legendItemFormatter: this._legendItemFormatter });
        }
        get extraLegendFormatOpts() {
            return this._extraLegendFormatOpts;
        }
        set extraLegendFormatOpts(value) {
            this._extraLegendFormatOpts = value;
            this.onLegendChange({ extraLegendFormatOpts: this._extraLegendFormatOpts });
        }
        get legendChanged$() {
            // perf if needed + scheduler or pipe in a debounce with all props
            return this._legendChangedSubject.asObservable();
        }
        onLegendChange(legendChange) {
            this._legendChangedSubject.next(legendChange);
        }
    }
    return ChartLegendFormatterMixin;
};
class LegendFormatter {
    constructor() { }
}
const _ChartLegendBase = mixinLegendFormatter(LegendFormatter);
class ChartLegendDirective extends _ChartLegendBase {
}

/**
 * legend with a border that matches the series and large number for a pie or
 * slice type chart.
 */
class SliceChartLegendDirective extends _ChartLegendBase {
    constructor() {
        super();
    }
    ngOnInit() {
        this.extraLegendFormatOpts = {
            itemMarginBottom: 10,
            itemDistance: 0,
            symbolHeight: 0.001,
            useHTML: true,
            enabled: true,
        };
        this.legendItemFormatter = partial(this.sliceChartLegendItemFormatter, this.precision, this.uom, this.customValueFormatter);
    }
    ngOnChanges({ precision, uom, customValueFormatter }) {
        if (precision?.currentValue != null && !precision.firstChange ||
            uom?.currentValue != null && !uom.firstChange ||
            customValueFormatter?.currentValue != null && !customValueFormatter.firstChange) {
            this.legendItemFormatter = partial(this.sliceChartLegendItemFormatter, this.precision, this.uom, this.customValueFormatter);
        }
    }
    sliceChartLegendItemFormatter(precision, uom, customValueFormatter) {
        const point = this['options'];
        let value = point.y;
        if (customValueFormatter) {
            const formattedValue = customValueFormatter(value);
            uom = formattedValue.unit;
            value = formattedValue.value;
        }
        const item = `<div style="display: flex; flex-direction: column; border-left: 5px solid ${point.color}; padding: 1px 10px;">
                    <span style="font-size: 1.5em">${value.toFixed(precision)}${uom ? ' ' + uom : ''}</span>
                    <span>${point.name}</span>
                  </div>`;
        return item;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SliceChartLegendDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.1", type: SliceChartLegendDirective, selector: "avn-slice-chart-legend", inputs: { layout: "layout", align: "align", verticalAlign: "verticalAlign", precision: "precision", uom: "uom", customValueFormatter: "customValueFormatter" }, providers: [
            {
                provide: ChartLegendDirective,
                useExisting: forwardRef((() => SliceChartLegendDirective)),
            }
        ], usesInheritance: true, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SliceChartLegendDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'avn-slice-chart-legend',
                    providers: [
                        {
                            provide: ChartLegendDirective,
                            useExisting: forwardRef((() => SliceChartLegendDirective)),
                        }
                    ],
                    inputs: ['layout', 'align', 'verticalAlign', 'precision', 'uom', 'customValueFormatter']
                }]
        }], ctorParameters: function () { return []; } });

/**
 * simple chart legend
 */
class SimpleLegendDirective extends _ChartLegendBase {
    constructor() {
        super();
    }
    ngOnInit() {
        this.extraLegendFormatOpts = { enabled: true };
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SimpleLegendDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.1", type: SimpleLegendDirective, selector: "avn-simple-legend", inputs: { layout: "layout", align: "align", verticalAlign: "verticalAlign" }, providers: [
            {
                provide: ChartLegendDirective,
                useExisting: forwardRef((() => SimpleLegendDirective)),
            }
        ], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SimpleLegendDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'avn-simple-legend',
                    providers: [
                        {
                            provide: ChartLegendDirective,
                            useExisting: forwardRef((() => SimpleLegendDirective)),
                        }
                    ],
                    inputs: ['layout', 'align', 'verticalAlign']
                }]
        }], ctorParameters: function () { return []; } });

class SimpleChartTooltipFormatterDirective extends _ChartTooltipBase {
    constructor() {
        super();
    }
    ngOnInit() {
        this.formatTooltipItem = this.formatToolTip;
    }
    ngOnChanges({ labelValueType }) {
        if (labelValueType) {
            /** supports percent or the actual value */
            this.labelvalueProp = labelValueType.currentValue === 'percent' ? 'percentage' : 'y';
        }
    }
    formatToolTip(tooltip) {
        // we use a formatter here since highchart tooltip.update won't work
        // as well, this ensures we can extend the model to use template refs.
        // todo add a template function and envoke it here
        const self = tooltip.options.self;
        // highcharts calls this with call, or bind so this is Highcharts.TooltipFormatterContextObject
        const tooltipVals = this;
        const tooltipValue = tooltipVals[self.tooltipFormat.labelvalueProp];
        return `<b>${tooltipVals.key}</b><br>${tooltipValue.toFixed(tooltip.options.valueDecimals)} ${tooltip.options.valueSuffix}`;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SimpleChartTooltipFormatterDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.1", type: SimpleChartTooltipFormatterDirective, selector: "avn-simple-chart-tooltip-formatter", inputs: { precision: "precision", uom: "uom", labelValueType: "labelValueType" }, providers: [
            {
                provide: ChartTooltipDirective,
                useExisting: forwardRef((() => SimpleChartTooltipFormatterDirective)),
            },
        ], usesInheritance: true, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SimpleChartTooltipFormatterDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'avn-simple-chart-tooltip-formatter',
                    providers: [
                        {
                            provide: ChartTooltipDirective,
                            useExisting: forwardRef((() => SimpleChartTooltipFormatterDirective)),
                        },
                    ],
                    inputs: ['precision', 'uom'],
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { labelValueType: [{
                type: Input
            }] } });

class TimeseriesChartTooltipFormatterDirective extends _ChartTooltipBase {
    constructor() {
        super();
    }
    ngOnInit() {
        this.headerFormat = `<span>{point.key}</span>`;
        this.pointFormat = `<h4 style="margin-bottom: 0">{point.y}</h4>`;
        this.labelvalueProp = 'y';
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: TimeseriesChartTooltipFormatterDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.1", type: TimeseriesChartTooltipFormatterDirective, selector: "avn-timeseries-chart-tooltip-formatter", inputs: { precision: "precision", uom: "uom", xDateFormat: "xDateFormat" }, providers: [
            {
                provide: ChartTooltipDirective,
                useExisting: forwardRef((() => TimeseriesChartTooltipFormatterDirective)),
            },
        ], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: TimeseriesChartTooltipFormatterDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'avn-timeseries-chart-tooltip-formatter',
                    providers: [
                        {
                            provide: ChartTooltipDirective,
                            useExisting: forwardRef((() => TimeseriesChartTooltipFormatterDirective)),
                        },
                    ],
                    inputs: ['precision', 'uom', 'xDateFormat'],
                }]
        }], ctorParameters: function () { return []; } });

/**
 * Legend for timeseries type chart with the option to show the average value.
 */
class TimeseriesLegendDirective extends _ChartLegendBase {
    constructor() {
        super();
    }
    ngOnInit() {
        this.extraLegendFormatOpts = {
            itemMarginBottom: 10,
            itemDistance: 10,
            symbolHeight: 0.001,
            symbolWidth: 0,
            useHTML: true,
            enabled: true,
        };
        // If precision is not provided, formatter will be set with the default precision as 0
        this.legendItemFormatter = partial(this.serieItemFormatter, this.precision, this.uom, this.averageLabel);
    }
    ngOnChanges({ precision, uom, averageLabel }) {
        if (precision?.currentValue != null && !precision.firstChange ||
            uom?.currentValue != null && !uom.firstChange ||
            averageLabel?.currentValue != null && !averageLabel.firstChange) {
            this.legendItemFormatter = partial(this.serieItemFormatter, this.precision, this.uom, this.averageLabel);
        }
    }
    serieItemFormatter(precision, uom, averageLabel) {
        const serie = this['options'];
        let item = '';
        if (serie?.data?.length > 0) {
            item = `<div style="border-left: 5px solid ${serie.color}; padding: 1px 10px;">
                  <h4>${serie.data[serie.data.length - 1].y.toFixed(precision)}${uom ? ' ' + uom : ''}</h4>
                  <p>${serie.name}</p>`;
            if (averageLabel) {
                const average = list => list?.length > 0 ? list.reduce((prev, curr) => prev + curr) / list.length : 0;
                item += `<hr /><h6>${average(serie.data.map(datum => datum.y)).toFixed(precision)}${uom ? ' ' + uom : ''}</h6><p>${averageLabel}</p>`;
            }
            item += `</div>`;
        }
        return item;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: TimeseriesLegendDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.1", type: TimeseriesLegendDirective, selector: "avn-timeseries-legend", inputs: { layout: "layout", align: "align", verticalAlign: "verticalAlign", precision: "precision", uom: "uom", averageLabel: "averageLabel" }, providers: [
            {
                provide: ChartLegendDirective,
                useExisting: forwardRef((() => TimeseriesLegendDirective)),
            }
        ], usesInheritance: true, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: TimeseriesLegendDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'avn-timeseries-legend',
                    providers: [
                        {
                            provide: ChartLegendDirective,
                            useExisting: forwardRef((() => TimeseriesLegendDirective)),
                        }
                    ],
                    inputs: ['layout', 'align', 'verticalAlign', 'precision', 'uom']
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { averageLabel: [{
                type: Input
            }] } });

const DEFAULT_DATAVIZ_Y_AXIS = {
    title: {
        text: undefined,
    },
    ceiling: undefined,
    softMin: undefined,
    min: undefined,
    max: undefined,
    softMax: undefined,
    floor: undefined,
    tickInterval: undefined,
};
const mixinYAxisProperties = (Base) => {
    class ChartYAxisPropertiesMixin extends Base {
        constructor() {
            super(...arguments);
            this._title = undefined;
            this._ceiling = undefined;
            this._floor = undefined;
            this._min = undefined;
            this._max = undefined;
            this._softMin = undefined;
            this._softMax = undefined;
            this._tickInterval = undefined;
            this.yAxisChangedSubject = new ReplaySubject();
        }
        get title() {
            return this._title;
        }
        set title(value) {
            if (this._title !== value) {
                this._title = value;
                this.onYAxisChange({ title: this._title });
            }
        }
        get ceiling() {
            return this._ceiling;
        }
        set ceiling(value) {
            if (this._ceiling !== value) {
                this._ceiling = value;
                this.onYAxisChange({ ceiling: value });
            }
        }
        get floor() {
            return this._floor;
        }
        set floor(value) {
            if (this._floor !== value) {
                this._floor = value;
                this.onYAxisChange({ floor: value });
            }
        }
        get min() {
            return this._min;
        }
        set min(value) {
            if (this._min !== value) {
                this._min = value;
                this.onYAxisChange({ min: value });
            }
        }
        get max() {
            return this._max;
        }
        set max(value) {
            if (this._max !== value) {
                this._max = value;
                this.onYAxisChange({ max: value });
            }
        }
        get softMin() {
            return this._softMin;
        }
        set softMin(value) {
            if (this._softMin !== value) {
                this._softMin = value;
                this.onYAxisChange({ softMin: value });
            }
        }
        get softMax() {
            return this._softMax;
        }
        set softMax(value) {
            if (this._softMax !== value) {
                this._softMax = value;
                this.onYAxisChange({ softMax: value });
            }
        }
        get tickInterval() {
            return this._tickInterval;
        }
        set tickInterval(value) {
            if (this._tickInterval !== value) {
                this._tickInterval = value;
                this.onYAxisChange({ tickInterval: value });
            }
        }
        get yAxisChanged$() {
            return this.yAxisChangedSubject.asObservable();
        }
        onYAxisChange(yAxisChange) {
            this.yAxisChangedSubject.next(yAxisChange);
        }
    }
    return ChartYAxisPropertiesMixin;
};
class YAxisPropertiesBase {
    constructor() { }
}
const _ChartYAxisBase = mixinYAxisProperties(YAxisPropertiesBase);
class ChartYAxisDirective extends _ChartYAxisBase {
}

/**
 * Simple y-axis directive
 */
class SimpleYAxisDirective extends _ChartYAxisBase {
    constructor() {
        super();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SimpleYAxisDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.1", type: SimpleYAxisDirective, selector: "avn-simple-y-axis", inputs: { title: "title", ceiling: "ceiling", softMin: "softMin", min: "min", max: "max", softMax: "softMax", floor: "floor", tickInterval: "tickInterval" }, providers: [
            {
                provide: ChartYAxisDirective,
                useExisting: forwardRef((() => SimpleYAxisDirective)),
            },
        ], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SimpleYAxisDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'avn-simple-y-axis',
                    providers: [
                        {
                            provide: ChartYAxisDirective,
                            useExisting: forwardRef((() => SimpleYAxisDirective)),
                        },
                    ],
                    inputs: ['title', 'ceiling', 'softMin', 'min', 'max', 'softMax', 'floor', 'tickInterval'],
                }]
        }], ctorParameters: function () { return []; } });

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function hcModuleFactory() { return [more, solidgauge, sunburst, more]; }
class ChartCoreModule {
    constructor(cls) {
        this.cls = cls;
        this.cls.initModules();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ChartCoreModule, deps: [{ token: ChartLibService }], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.1", ngImport: i0, type: ChartCoreModule, declarations: [SplineSeriesDirective,
            LineSeriesDirective,
            StepLineSeriesDirective,
            AreaRangeSeriesDirective,
            SliceChartTooltipFormatterDirective,
            SimpleChartTooltipFormatterDirective,
            TimeseriesChartTooltipFormatterDirective,
            SliceChartLegendDirective,
            SimpleLegendDirective,
            TimeseriesLegendDirective,
            SimpleYAxisDirective], imports: [CommonModule, CoreModule], exports: [SplineSeriesDirective,
            LineSeriesDirective,
            StepLineSeriesDirective,
            AreaRangeSeriesDirective,
            SliceChartTooltipFormatterDirective,
            SimpleChartTooltipFormatterDirective,
            TimeseriesChartTooltipFormatterDirective,
            SliceChartLegendDirective,
            SimpleLegendDirective,
            TimeseriesLegendDirective,
            SimpleYAxisDirective] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ChartCoreModule, providers: [ChartLibService, ChartThemeAdapterService, { provide: HIGHCHARTS_MODULES, useFactory: hcModuleFactory }], imports: [CommonModule, CoreModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ChartCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SplineSeriesDirective,
                        LineSeriesDirective,
                        StepLineSeriesDirective,
                        AreaRangeSeriesDirective,
                        SliceChartTooltipFormatterDirective,
                        SimpleChartTooltipFormatterDirective,
                        TimeseriesChartTooltipFormatterDirective,
                        SliceChartLegendDirective,
                        SimpleLegendDirective,
                        TimeseriesLegendDirective,
                        SimpleYAxisDirective,
                    ],
                    imports: [CommonModule, CoreModule],
                    providers: [ChartLibService, ChartThemeAdapterService, { provide: HIGHCHARTS_MODULES, useFactory: hcModuleFactory }],
                    exports: [
                        SplineSeriesDirective,
                        LineSeriesDirective,
                        StepLineSeriesDirective,
                        AreaRangeSeriesDirective,
                        SliceChartTooltipFormatterDirective,
                        SimpleChartTooltipFormatterDirective,
                        TimeseriesChartTooltipFormatterDirective,
                        SliceChartLegendDirective,
                        SimpleLegendDirective,
                        TimeseriesLegendDirective,
                        SimpleYAxisDirective,
                    ],
                }]
        }], ctorParameters: function () { return [{ type: ChartLibService }]; } });

/**
 * base chart mixin
 * todo: this is almost a base class need to seperate by behavior...
 */
function mixinChartBase(base) {
    class ChartBaseMixin extends base {
        get title() {
            return this._title;
        }
        set title(value) {
            this._title = value;
            this.chartOptions.title.text = value;
            this.chart?.title.update({ text: value }, false);
        }
        get subtitle() {
            return this._subtitle;
        }
        set subtitle(value) {
            this._subtitle = value;
            this.chartOptions.subtitle.text = value;
            this.chart?.subtitle.update({ text: value }, false);
        }
        get titlePosition() {
            return this._titlePosition;
        }
        set titlePosition(value) {
            this._titlePosition = value;
            this.chartOptions.title.align = value?.horizontal;
            this.chartOptions.title.verticalAlign = value?.vertical;
            this.chartOptions.subtitle.align = value?.horizontal;
            this.chartOptions.subtitle.verticalAlign = value?.vertical;
            this.chart?.title.update({ align: value?.horizontal, verticalAlign: value?.vertical }, false);
            this.chart?.subtitle.update({ align: value?.horizontal, verticalAlign: value?.vertical }, false);
        }
        onResize() {
            this.chart?.reflow();
        }
        get chartReady$() {
            return this._chartReadySubject.asObservable().pipe(filter((ready) => !!ready));
        }
        get isChartReady() {
            return this._chartReadySubject.value;
        }
        onChartReady() {
            this._chartReadySubject.next(true);
        }
        mergeChartSettings(opts) {
            // note that this cannot be done in mixins and needs to be called prior to chart initializations
            // as highchart will add the recursion and mutate props, we can't do this in the tooltip mixins.
            const safeOptions = {};
            opts.forEach((opt) => {
                const noRecurseOpt = { ...opt };
                if (opt.tooltip) {
                    const tooltipFormatProps = { tooltipFormat: opt.tooltip.self?.tooltipFormat };
                    delete noRecurseOpt.tooltip?.self;
                    noRecurseOpt.tooltip.self = tooltipFormatProps;
                }
                Object.assign(safeOptions, noRecurseOpt);
            });
            return safeOptions;
        }
        constructor(...args) {
            super(...args);
            this._chartReadySubject = new BehaviorSubject(false);
            this.chartOptions = {
                title: {
                    text: '',
                },
                subtitle: {
                    text: '',
                },
                chart: {
                    margin: [0, 0, 0, 0],
                    spacingTop: 0,
                    spacingRight: 0,
                    spacingBottom: 0,
                    spacingLeft: 0,
                    plotBorderWidth: 0,
                },
            };
            this._title = this.chartOptions.title.text;
            this._subtitle = this.chartOptions.subtitle.text;
            this._titlePosition = {
                horizontal: 'left',
                vertical: 'top',
            };
        }
    }
    return ChartBaseMixin;
}

class PieChart {
    constructor(_chartThemeAdapterService) {
        this._chartThemeAdapterService = _chartThemeAdapterService;
    }
}

/**
 * Chart y-axis mixin
 */
function mixinYAxisFeatures(base) {
    class YAxisFeaturesMixin extends base {
        constructor(...args) {
            super(...args);
            this.bInstance = new base();
            this._that = this;
            // extend the options in case the prior mixin defined them
            this.chartOptions = this.bInstance.hasOwnProperty('chartOptions')
                ? Object.assign({
                    yAxis: {
                        ...DEFAULT_DATAVIZ_Y_AXIS,
                        ...this.bInstance['chartOptions'].yAxis,
                    },
                }, this.bInstance['chartOptions'])
                : Object.assign({
                    yAxis: {
                        ...DEFAULT_DATAVIZ_Y_AXIS,
                    },
                });
            this._yAxisSettings = { ...DEFAULT_DATAVIZ_Y_AXIS };
        }
        get yAxisSettings() {
            return this._yAxisSettings;
        }
        set yAxisSettings(value) {
            this._yAxisSettings = value;
            Object.assign(this.chartOptions.yAxis, value);
            this.chart?.yAxis[0].update({ ...value }, true);
        }
        /** applies y-axis formats to the chart and options */
        yAxisSync(yAxisFormatter, destroyed$) {
            yAxisFormatter?.yAxisChanged$.pipe(takeUntil(destroyed$)).subscribe((yAxisSettings) => {
                this.yAxisSettings = { ...Object.assign(this.yAxisSettings, yAxisSettings) };
            });
        }
        resetYAxis() {
            Object.assign(this.chartOptions.yAxis, DEFAULT_DATAVIZ_Y_AXIS);
            this.chart?.yAxis[0].update({ ...DEFAULT_DATAVIZ_Y_AXIS }, true);
        }
    }
    return YAxisFeaturesMixin;
}

/**
 * utility function to facilitate using theming in charts without re-rendering
 * the chart
 */
const themeTypeToCssVar = (themeType, variant = 'base') => `var(--${themeType}-${variant})`;

class SunburstComponent {
    constructor(chartThemeAdapterService) {
        this.chartThemeAdapterService = chartThemeAdapterService;
        /**
         * Charts config
         */
        this._chartOpts = {
            title: { text: 'Restaurant 1' },
            subtitle: { text: 'Restaurant 1' },
            series: [
                {
                    type: 'sunburst',
                    data: [
                        {
                            id: '0',
                            parent: '',
                            name: 'All',
                            color: 'rgb(27, 162, 247)',
                        },
                        { id: '1.1', parent: '0', name: 'Indoor', color: '' },
                        { id: '1.2', parent: '0', name: 'Outdoor', color: '' },
                        { id: '1.1.1', parent: '1.1', name: 'OK', value: 25, color: 'green' },
                        { id: '1.1.2', parent: '1.1', name: 'Error', value: 1, color: 'red' },
                        {
                            id: '1.1.3',
                            parent: '1.1',
                            name: 'Warning',
                            value: 22,
                            color: 'orange',
                        },
                        { id: '1.2.1', parent: '1.2', name: 'OK', value: 25, color: 'green' },
                        {
                            id: '1.2.2',
                            parent: '1.2',
                            name: 'Error',
                            value: 10,
                            color: 'red',
                        },
                        {
                            id: '1.2.3',
                            parent: '1.2',
                            name: 'Warning',
                            value: 36,
                            color: 'orange',
                        },
                    ],
                    cursor: 'pointer',
                    dataLabels: {
                        // formatter: datalab,
                        format: '{point.name} - {point.value}',
                        filter: { operator: '>', property: 'outerArcLength', value: 50 },
                    },
                    allowTraversingTree: true,
                    levelIsConstant: false,
                },
            ],
            tooltip: {
                headerFormat: '{point.name}',
                pointFormat: '<b>{point.value} devices in {point.name}</b> health status.',
            },
        };
        this.componentDestroyed$ = new Subject();
    }
    ngOnChanges({ title, subtitle }) {
        if (title?.currentValue) {
            this._chartOpts.title.text = title.currentValue;
            this._chart?.title.update({ text: title.currentValue });
        }
        if (subtitle?.currentValue) {
            this._chartOpts.subtitle.text = subtitle.currentValue;
            this._chart?.title.update({ text: subtitle.currentValue });
        }
    }
    ngOnInit() {
        this.chartThemeAdapterService.chartTheme$.pipe(takeUntil(this.componentDestroyed$)).subscribe((r) => {
            this._chart?.update(r, true);
        });
        // we should have this abstracted by chart theme
        //
        this.chartThemeAdapterService.chartThemeColors$.pipe(takeUntil(this.componentDestroyed$)).subscribe((theme) => {
            // todo remove this temp madness BARRRRFFF
            // impl dataParser or something
            const series = this._chartOpts.series[0];
            series.data[0].color = theme.infoBase;
            series.data[3].color = theme.successBase;
            series.data[4].color = theme.dangerBase;
            series.data[5].color = theme.warningBase;
            series.data[6].color = theme.successBase;
            series.data[7].color = theme.dangerBase;
            series.data[8].color = theme.warningBase;
            this._chart?.update(this._chartOpts, true);
        });
    }
    ngAfterViewInit() {
        this._chart = HighCharts.chart(this.chartContainer.nativeElement, this._chartOpts, (_) => {
            // stfu typescript
            // console.log(foo);
        });
        // this._chart.subtitle.update({text: 'foo'});
    }
    redraw() {
        // TODO in base chart we would use a mutation observer on the container
        // or avn-resize and redraw the chart to the dimensions of the parent.
        this._chart.reflow();
        // this._chart.setSize();
    }
    ngOnDestroy() {
        if (this._chart) {
            this._chart.destroy();
        }
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SunburstComponent, deps: [{ token: ChartThemeAdapterService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.1", type: SunburstComponent, selector: "avn-sunburst", inputs: { title: "title", subtitle: "subtitle" }, viewQueries: [{ propertyName: "chartContainer", first: true, predicate: ["chartContainer"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div #chartContainer></div>\n", styles: [":host{display:block;width:100%;height:100%}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SunburstComponent, decorators: [{
            type: Component,
            args: [{ selector: 'avn-sunburst', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div #chartContainer></div>\n", styles: [":host{display:block;width:100%;height:100%}\n"] }]
        }], ctorParameters: function () { return [{ type: ChartThemeAdapterService }]; }, propDecorators: { chartContainer: [{
                type: ViewChild,
                args: ['chartContainer', { static: false }]
            }], title: [{
                type: Input
            }], subtitle: [{
                type: Input
            }] } });

class SunburstModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SunburstModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.1", ngImport: i0, type: SunburstModule, declarations: [SunburstComponent], imports: [CommonModule, ChartCoreModule, CoreModule, ButtonModule], exports: [SunburstComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SunburstModule, imports: [CommonModule, ChartCoreModule, CoreModule, ButtonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SunburstModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SunburstComponent],
                    imports: [CommonModule, ChartCoreModule, CoreModule, ButtonModule],
                    exports: [SunburstComponent],
                }]
        }] });

/**
 * series chart mixin
 * adds series child content parsing to
 */
function mixinCartesianSeriesChart(base) {
    class CartesianSeriesChartMixin extends base {
        /**
         * adds the series to the chart and syncs them when they change
         */
        enableSeriesSync(series, destroyed$) {
            // the debounce allows to control the rate of the redraws
            this._redrawSubject.pipe(debounceTime(100), takeUntil(destroyed$)).subscribe(() => this.chart.redraw());
            // add the initial series to the chart (this occurs afterContentInit but the series data could be async)
            // Note: it seems that animations are only working for series initially added in the chart options at chart creation time.
            // And not working for series with addSeries once the chart is initialized)
            series.forEach((serie) => {
                const initialSeries = this.toHighChartSerie(serie);
                initialSeries.forEach((si) => {
                    this.chartOptions.series.push(si);
                    // maintain a key / val store and clear these
                    // watch the serie for data change
                    this._seriesHashes[serie.key] = {
                        dataChangedSubscription: this.createSerieSubscription(serie, destroyed$),
                    };
                });
            });
            this._seriesInitialized$.next(true);
            // handle add / remove series
            series.changes
                .pipe(map((newSeries) => newSeries.toArray()), takeUntil(destroyed$))
                .subscribe((newSeries) => {
                const currentKeys = Object.keys(this._seriesHashes);
                const addedSeries = newSeries.filter((el) => !currentKeys.includes(el.key));
                const removedSeriesKeys = currentKeys.filter((el) => !newSeries.map((s) => s.key).includes(el));
                // removed series
                removedSeriesKeys.forEach((sk) => {
                    // dont redraw right away
                    this.chart.get(sk).remove(false);
                    this.chart.get(`${sk}-range`)?.remove(false);
                    this._seriesHashes[sk].dataChangedSubscription.unsubscribe();
                    delete this._seriesHashes[sk];
                });
                // added series
                addedSeries.forEach((addedSerie) => {
                    // dont redraw right away
                    const seriesToAdd = this.toHighChartSerie(addedSerie);
                    seriesToAdd.forEach((sa) => {
                        this.chart.addSeries(sa, false, true);
                    });
                    this._seriesHashes[addedSerie.key] = {
                        dataChangedSubscription: this.createSerieSubscription(addedSerie, destroyed$),
                    };
                });
                // redraw once all series data have been updated
                this._redrawSubject.next();
            });
        }
        createSerieSubscription(serie, destroyed$) {
            // individual changes
            // todo do I need to individualize changes
            // static data sources trigger a change before
            return combineLatest([this._seriesInitialized$, serie.seriesChanged$])
                .pipe(takeUntil(destroyed$))
                .subscribe(([, seriesChangedData]) => {
                // handle the changes.
                // some attributes need a redraw
                const redraw = ['data', 'color', 'lineWidth', 'fillOpacity'].some((prop) => seriesChangedData.hasOwnProperty(prop));
                // get the edit chart serie
                const editedSerie = this.chart.get(serie.key);
                const rangeSerie = this.chart.get(`${serie.key}-range`);
                // use the setData update method for data update
                if (seriesChangedData.data) {
                    // compare with previous data
                    const newData = this._parseSerieData(seriesChangedData.data, serie.maxDataItems);
                    editedSerie.setData(newData, false);
                    rangeSerie?.setData(newData.map((data) => [data.x, data.minY, data.maxY]), false);
                    delete seriesChangedData.data;
                }
                // use the regular update method for other options update (name, color, etc..)
                editedSerie.update(this._applyDefaultOptions(seriesChangedData), false);
                // some props do not apply to associated range
                delete seriesChangedData.lineWidth;
                rangeSerie?.update(this._applyDefaultOptions(seriesChangedData), false);
                if (redraw) {
                    this._redrawSubject.next();
                }
            });
        }
        toHighChartSerie(series) {
            // todo factory pattern ? or not...
            // we need to set color scheme for a chart.
            // this uses the defined colors in Highcharts.getOptions().colors
            // see: https://api.highcharts.com/highcharts/colors
            switch (series.type) {
                case SeriesTypes.SPLINE:
                    return [
                        this._applyDefaultOptions({
                            id: series.key,
                            type: 'spline',
                            name: series.name,
                            color: series.color,
                            data: this._parseSerieData(series.data, series.maxDataItems),
                            ...(series.lineWidth ? { lineWidth: series.lineWidth } : {}),
                        }),
                    ];
                case SeriesTypes.AREA_SPLINE:
                    return [
                        this._applyDefaultOptions({
                            id: series.key,
                            type: 'areaspline',
                            name: series.name,
                            color: series.color,
                            data: this._parseSerieData(series.data, series.maxDataItems),
                            ...(series.lineWidth ? { lineWidth: series.lineWidth } : {}),
                            ...(series.areaFillOpacity ? { fillOpacity: series.areaFillOpacity } : {}),
                        }),
                    ];
                case SeriesTypes.LINE:
                    return [
                        this._applyDefaultOptions({
                            id: series.key,
                            type: 'line',
                            name: series.name,
                            color: series.color,
                            data: this._parseSerieData(series.data, series.maxDataItems),
                            ...(series.lineWidth ? { lineWidth: series.lineWidth } : {}),
                        }),
                    ];
                case SeriesTypes.AREA_LINE:
                    return [
                        this._applyDefaultOptions({
                            id: series.key,
                            type: 'area',
                            step: null,
                            name: series.name,
                            color: series.color,
                            data: this._parseSerieData(series.data, series.maxDataItems),
                            ...(series.lineWidth ? { lineWidth: series.lineWidth } : {}),
                            ...(series.areaFillOpacity ? { fillOpacity: series.areaFillOpacity } : {}),
                        }),
                    ];
                case SeriesTypes.STEP_LINE:
                    return [
                        this._applyDefaultOptions({
                            id: series.key,
                            type: 'line',
                            step: 'left',
                            name: series.name,
                            color: series.color,
                            data: this._parseSerieData(series.data, series.maxDataItems),
                        }),
                    ];
                case SeriesTypes.AREA_STEP_LINE:
                    return [
                        this._applyDefaultOptions({
                            id: series.key,
                            type: 'area',
                            step: 'left',
                            name: series.name,
                            color: series.color,
                            data: this._parseSerieData(series.data, series.maxDataItems),
                            ...(series.lineWidth ? { lineWidth: series.lineWidth } : {}),
                            ...(series.areaFillOpacity ? { fillOpacity: series.areaFillOpacity } : {}),
                        }),
                    ];
                case SeriesTypes.AREA_RANGE_SPLINE:
                case SeriesTypes.AREA_RANGE_LINE:
                    const areaSeries = [];
                    areaSeries.push(this._applyDefaultOptions({
                        id: series.key,
                        type: series.type === SeriesTypes.AREA_RANGE_SPLINE ? 'spline' : 'line',
                        name: series.name,
                        color: series.color,
                        data: this._parseSerieData(series.data, series.maxDataItems),
                        ...(series.lineWidth ? { lineWidth: series.lineWidth } : {}),
                    })); // spline or line IIF exists....
                    areaSeries.push(this._applyDefaultOptions({
                        id: `${series.key}-range`,
                        name: 'Range',
                        data: this._parseSerieData(series.data.map((data) => [data.x, data.minY, data.maxY]), series.maxDataItems),
                        type: series.type === SeriesTypes.AREA_RANGE_SPLINE ? 'areasplinerange' : 'arearange',
                        linkedTo: ':previous',
                        color: series.color,
                        ...(series.areaFillOpacity ? { fillOpacity: series.areaFillOpacity } : {}),
                        zIndex: 0,
                    })); // min-max as area series
                    return areaSeries;
                default:
                    return;
            }
        }
        // TODO it takes a rbg or hex in highcharts to have the opacity work. to be able to use css vars or ThemeTypes in color input,
        //  get the actual color value from the current theme stored in highchart options
        themeToColor(color) {
            const theme = HighCharts.getOptions()['custom'];
            if (color.startsWith('var(--')) {
                const cssVar = color.substring(4, color.lastIndexOf(')'));
                return theme[cssVariableToThemeProperty(cssVar)];
            }
            if (Object.values(ThemeType).includes(color)) {
                return theme[cssVariableToThemeProperty(`--${color}-base`)];
            }
            return color;
        }
        /** Applies default options when no value is set (to any of the options declared in obj) **/
        _applyDefaultOptions(obj) {
            if (obj.hasOwnProperty('color')) {
                obj.color = this.themeToColor(obj.color || HighCharts.getOptions().colors[0]);
            }
            return obj;
        }
        _parseSerieData(data, maxDataItems) {
            return maxDataItems > 0 && data.length > maxDataItems ? data.slice(data.length - maxDataItems) : data;
        }
        constructor(...args) {
            super(...args);
            // todo function to chain the types ? or use different values ?
            this.bInstance = new base();
            this.seriesProps = { series: [] };
            // extend the options incase the prior mixin defined them
            this.chartOptions = this.bInstance.hasOwnProperty('chartOptions') ? Object.assign(this.seriesProps, this.bInstance['chartOptions']) : this.seriesProps;
            /**
             * @docs-private
             * @ignore
             * we add a uuid to track the ordinal postion of the chart series and subscriptions
             */
            this._seriesHashes = {};
            /**
             * @docs-private
             * @ignore
             * we need to defer initialization of the chart series internally so the rendering
             * can stay efficient
             * todo: make this public api so charts are init when all series have one value emitted...
             */
            this._seriesInitialized$ = new BehaviorSubject(false);
            /**
             * @docs-private
             * @ignore
             * multiple series changes are tracked separately so we use a subject to debounce the amount of redraws,
             * as they usually occur at the same time
             */
            this._redrawSubject = new Subject();
        }
    }
    return CartesianSeriesChartMixin;
}

/**
 * chart theming functionality
 * mostly a test for a second mixin
 * probably that this is on all charts for theming....
 *
 * but
 */
const mixinChartTheming = (base) => {
    class ThemableChartMixin extends base {
        constructor(...args) {
            super(...args);
        }
    }
    return ThemableChartMixin;
};

/**
 * legend chart mixin
 */
function mixinLegendChart(base) {
    class LegendChartMixin extends base {
        constructor(...args) {
            super(...args);
            this.bInstance = new base();
            this._that = this;
            // default legend options
            this.defaultLegendOptions = {
                enabled: true,
                layout: 'horizontal',
                align: 'left',
                itemMarginTop: 2,
                itemMarginBottom: 2,
            };
            // extend the options in case the prior mixin defined them
            this.chartOptions = this.bInstance.hasOwnProperty('chartOptions')
                ? Object.assign({
                    legend: {
                        ...this.defaultLegendOptions,
                        ...this.bInstance['chartOptions'].legend,
                    },
                    responsive: {
                        rules: [{
                                condition: {
                                    maxWidth: 700
                                },
                                chartOptions: {
                                    legend: {
                                        itemStyle: {
                                            'font-size': 'smaller'
                                        }
                                    }
                                }
                            }, {
                                condition: {
                                    maxWidth: 400
                                },
                                chartOptions: {
                                    legend: {
                                        itemStyle: {
                                            'font-size': 'x-small'
                                        }
                                    }
                                }
                            }]
                    }
                }, this.bInstance['chartOptions'])
                : Object.assign({
                    legend: {
                        ...this.defaultLegendOptions,
                    },
                });
            this._legendFormat = {
                layout: 'horizontal',
                align: 'right',
                verticalAlign: 'middle',
            };
        }
        get legendFormat() {
            return this._legendFormat;
        }
        set legendFormat(value) {
            const hcOptions = {
                enabled: true,
                verticalAlign: value.verticalAlign,
                layout: value.layout,
                align: value.align,
            };
            this._legendFormat = value;
            if (value.extraLegendFormatOpts) {
                Object.assign(hcOptions, value.extraLegendFormatOpts);
            }
            if (value.legendItemFormatter) {
                Object.assign(hcOptions, { labelFormatter: value.legendItemFormatter });
            }
            Object.assign(this.chartOptions.legend, hcOptions);
            this.chart?.legend.update({ ...hcOptions }, true);
        }
        /** applies legend formats to the chart and options */
        legendSync(legendFormatter, destroyed$) {
            legendFormatter?.legendChanged$.pipe(takeUntil(destroyed$)).subscribe((legendSettings) => {
                this.legendFormat = { ...Object.assign(this.legendFormat, legendSettings) };
            });
        }
        disableLegend() {
            this.chartOptions.legend.enabled = false;
            this.chart?.legend.update({ ...this.chartOptions.legend });
        }
    }
    return LegendChartMixin;
}

/**
 * This mixin decorates charts with a common interface for tooltips
 * it also ensures that tooltip formatters can interact with the tooltip formatters
 * each chart implementation should expose the properties that are applicable and construct the formatter
 */
function mixinTooltipChartBehavior(base) {
    class TooltipFormatMixin extends base {
        constructor(...args) {
            super(...args);
            this.bInstance = new base();
            this._that = this;
            this.defaultTooltipOptions = {
                animation: true,
                shared: false,
                useHTML: true,
                outside: true,
                valueDecimals: 0,
                valueSuffix: '',
                xDateFormat: '%Y-%m-%d %H:%M:%S',
                headerFormat: undefined,
                pointFormat: undefined,
                footerFormat: '',
                self: this,
            };
            // extend the options in case the prior mixin defined them
            this.chartOptions = this.bInstance.hasOwnProperty('chartOptions')
                ? Object.assign({
                    tooltip: {
                        ...this.defaultTooltipOptions,
                        ...this.bInstance['chartOptions'].tooltip,
                    },
                }, this.bInstance['chartOptions'])
                : Object.assign({
                    tooltip: {
                        ...this.defaultTooltipOptions,
                    },
                });
            this._tooltipFormat = {
                precision: 0,
                labelvalueProp: 'y',
                uom: '',
                animation: true,
                shared: false,
                xDateFormat: '%Y-%m-%d %H:%M:%S',
                headerFormat: undefined,
                pointFormat: undefined,
                footerFormat: '',
            };
        }
        get tooltipFormat() {
            return this._tooltipFormat;
        }
        set tooltipFormat(value) {
            this._tooltipFormat = value;
            this.chartOptions.tooltip.valueDecimals = value.precision;
            this.chartOptions.tooltip.valueSuffix = this.tooltipFormat.labelvalueProp === 'y' ? value.uom : '%';
            this.chartOptions.tooltip.formatter = value.formatTooltipItem;
            this.chartOptions.tooltip.shared = value.shared;
            this.chartOptions.tooltip.enabled = true;
            this.chartOptions.tooltip.animation = value.animation;
            this.chartOptions.tooltip.xDateFormat = value.xDateFormat;
            this.chartOptions.tooltip.headerFormat = value.headerFormat;
            this.chartOptions.tooltip.pointFormat = value.pointFormat;
            this.chartOptions.tooltip.footerFormat = value.footerFormat || ''; // This one cannot be null or undefined or highcharts throws error
            this.chart?.tooltip.update({ ...this.chartOptions.tooltip });
        }
        /** applies tooltip formats to the chart and options */
        tooltipSync(tooltipFormatter, destroyed$) {
            tooltipFormatter?.tooltipChanged$.pipe(takeUntil(destroyed$)).subscribe((tooltipSettings) => {
                this.tooltipFormat = { ...Object.assign(this.tooltipFormat, tooltipSettings) };
            });
        }
        disableTooltip() {
            this.chartOptions.tooltip.enabled = false;
            this.chart?.tooltip.update({ ...this.chartOptions.tooltip });
        }
    }
    return TooltipFormatMixin;
}

class TimeseriesChart {
    constructor(_chartThemeAdapterService) {
        this._chartThemeAdapterService = _chartThemeAdapterService;
    }
}
// note: constructors are invoked left to right.
// the nested one is called first.
const _SeriesChartBase = mixinChartTheming(mixinCartesianSeriesChart(mixinTooltipChartBehavior(mixinLegendChart(mixinYAxisFeatures(mixinChartBase(TimeseriesChart))))));
class TimeseriesComponent extends _SeriesChartBase {
    constructor(_chartThemeAdapterService) {
        super(_chartThemeAdapterService);
        /** Default line width for series. Can be overridden for each serie **/
        this.lineWidth = 2;
        /** Default area fill opacity for area series. Can be overridden for each serie **/
        this.areaFillOpacity = 1;
        this.componentDestroyed$ = new Subject();
    }
    _getPlotOptions(lineWidth, areaFillOpacity) {
        const getAreaOptions = (lineWidthValue, areaFillOpacityValue) => ({
            fillOpacity: areaFillOpacityValue,
            lineWidth: lineWidthValue,
            marker: {
                enabled: false,
            },
            states: {
                hover: {
                    lineWidth: 0,
                    lineColor: 'transparent',
                    halo: {
                        enabled: false,
                    },
                    animation: false,
                },
            },
        });
        return {
            area: getAreaOptions(lineWidth, areaFillOpacity),
            arearange: getAreaOptions(0, areaFillOpacity),
            areaspline: getAreaOptions(lineWidth, areaFillOpacity),
            areasplinerange: getAreaOptions(0, areaFillOpacity),
        };
    }
    ngOnInit() {
        this._timeSeriesChartOptions = {
            chart: { zooming: { type: 'x' } },
            time: {
                useUTC: !this.timezone,
                timezone: this.timezone,
            },
            xAxis: { type: 'datetime' },
            plotOptions: {
                ...this._getPlotOptions(this.lineWidth, this.areaFillOpacity),
            },
        };
        this._chartThemeAdapterService.chartTheme$.pipe(takeUntil(this.componentDestroyed$)).subscribe((r) => {
            this.chart?.update(r, true);
        });
        // we should have this abstracted by chart theme
        this._chartThemeAdapterService.chartThemeColors$.pipe(takeUntil(this.componentDestroyed$)).subscribe((_theme) => {
        });
    }
    ngOnChanges({ lineWidth, areaFillOpacity }) {
        if (this.isChartReady) {
            if (lineWidth) {
                const plotOptions = {
                    plotOptions: this._getPlotOptions(lineWidth.currentValue, this.areaFillOpacity),
                };
                this._timeSeriesChartOptions = {
                    ...this._timeSeriesChartOptions,
                    ...plotOptions,
                };
                // update the current series
                this.series?.toArray().forEach((serie) => (serie.lineWidth = lineWidth.currentValue));
            }
            if (areaFillOpacity) {
                const plotOptions = {
                    plotOptions: this._getPlotOptions(this.lineWidth, areaFillOpacity.currentValue),
                };
                this._timeSeriesChartOptions = {
                    ...this._timeSeriesChartOptions,
                    ...plotOptions,
                };
            }
        }
    }
    ngAfterViewInit() {
        const chartSettings = this.mergeChartSettings([this.chartOptions, this._timeSeriesChartOptions]);
        this.chart = HighCharts.chart(this.chartContainer.nativeElement, chartSettings, this.onChartReady.bind(this));
    }
    ngAfterContentInit() {
        this.yAxisFormatters.changes.pipe(map((yAxisOptions) => yAxisOptions.length > 0), startWith(this.yAxisFormatters.length > 0), takeUntil(this.componentDestroyed$)).subscribe((hasYAxisOptions) => {
            if (hasYAxisOptions) {
                this.yAxisSync(this.yAxisFormatters.first, this.componentDestroyed$);
            }
            else {
                this.resetYAxis();
            }
        });
        this.tooltipFormatters.changes.pipe(map((tooltips) => tooltips.length > 0), startWith(this.tooltipFormatters.length > 0), takeUntil(this.componentDestroyed$)).subscribe(hasTooltip => {
            if (hasTooltip) {
                this.tooltipSync(this.tooltipFormatters.first, this.componentDestroyed$);
            }
            else {
                this.disableTooltip();
            }
        });
        this.legendFormatters.changes.pipe(map((legend) => legend.length > 0), startWith(this.legendFormatters.length > 0), takeUntil(this.componentDestroyed$)).subscribe(hasLegend => {
            if (hasLegend) {
                this.legendSync(this.legendFormatters.first, this.componentDestroyed$);
            }
            else {
                this.disableLegend();
            }
        });
        this.enableSeriesSync(this.series, this.componentDestroyed$);
    }
    ngOnDestroy() {
        if (this.chart) {
            this.chart.destroy();
        }
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: TimeseriesComponent, deps: [{ token: ChartThemeAdapterService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.1", type: TimeseriesComponent, selector: "avn-timeseries", inputs: { title: "title", subtitle: "subtitle", lineWidth: "lineWidth", areaFillOpacity: "areaFillOpacity", timezone: "timezone" }, queries: [{ propertyName: "yAxisFormatters", predicate: ChartYAxisDirective }, { propertyName: "series", predicate: CartesianSeriesDirective }, { propertyName: "legendFormatters", predicate: ChartLegendDirective }, { propertyName: "tooltipFormatters", predicate: ChartTooltipDirective }], viewQueries: [{ propertyName: "chartContainer", first: true, predicate: ["chartContainer"], descendants: true, static: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div #chartContainer (avnResize)=\"onResize()\" class=\"chart-container\"></div>\n", styles: [":host{display:block;width:100%;height:100%}:host .chart-container{width:100%;height:100%}\n"], dependencies: [{ kind: "directive", type: i2.ResizeDirective, selector: "[avnResize]", inputs: ["debounceTime", "emitInitialEvent"], outputs: ["avnResize"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: TimeseriesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'avn-timeseries', inputs: ['title', 'subtitle'], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div #chartContainer (avnResize)=\"onResize()\" class=\"chart-container\"></div>\n", styles: [":host{display:block;width:100%;height:100%}:host .chart-container{width:100%;height:100%}\n"] }]
        }], ctorParameters: function () { return [{ type: ChartThemeAdapterService }]; }, propDecorators: { chartContainer: [{
                type: ViewChild,
                args: ['chartContainer', { static: true }]
            }], yAxisFormatters: [{
                type: ContentChildren,
                args: [ChartYAxisDirective]
            }], series: [{
                type: ContentChildren,
                args: [CartesianSeriesDirective]
            }], legendFormatters: [{
                type: ContentChildren,
                args: [ChartLegendDirective]
            }], tooltipFormatters: [{
                type: ContentChildren,
                args: [ChartTooltipDirective]
            }], lineWidth: [{
                type: Input
            }], areaFillOpacity: [{
                type: Input
            }], timezone: [{
                type: Input
            }] } });

class TimeseriesModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: TimeseriesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.1", ngImport: i0, type: TimeseriesModule, declarations: [TimeseriesComponent], imports: [CommonModule, CoreModule], exports: [TimeseriesComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: TimeseriesModule, imports: [CommonModule, CoreModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: TimeseriesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [TimeseriesComponent],
                    imports: [CommonModule, CoreModule],
                    exports: [TimeseriesComponent],
                }]
        }] });

// barf... workaround for empty pie charts
// https://github.com/highcharts/highcharts/issues/12663
// not happy but I rather have this than the async pipe not working
// https://jira.activia-networks.com/browse/UIC-290
// eslint-disable-next-line @typescript-eslint/no-empty-function
HighCharts.seriesTypes.pie.prototype.drawEmpty = function () { };
// note: constructors are invoked left to right.
// the nested one is called first.
const _ChartBase$3 = mixinChartTheming(mixinTooltipChartBehavior(mixinLegendChart(mixinChartBase(PieChart))));
class PieComponent extends _ChartBase$3 {
    constructor(_chartThemeAdapterService) {
        super(_chartThemeAdapterService);
        /**
         * enables the selection of items within the pie chart
         * selecting an item removes it from the chart and shows focus on other items.
         */
        this.selectionEnabled = false;
        /** Emit the name of the data when its serie or its legend is clicked */
        this.dataClicked = new EventEmitter();
        this.componentDestroyed$ = new Subject();
        this._id = v4();
    }
    ngOnInit() {
        this._chartThemeAdapterService.chartTheme$.pipe(takeUntil(this.componentDestroyed$)).subscribe((r) => {
            this.chart?.update(r, true);
        });
        // set the initial options and pie serie
        this._pieChartOptions = {
            chart: {
                type: 'pie',
            },
            plotOptions: {
                pie: {
                    allowPointSelect: this.selectionEnabled,
                    cursor: this.selectionEnabled ? 'pointer' : undefined,
                    dataLabels: {
                        enabled: this.showDataLabels,
                    },
                    showInLegend: true,
                },
                series: {
                    events: {
                        click: (event) => {
                            const datum = this.data.find(d => d.name === event.point.name);
                            this.dataClicked.emit(datum);
                        },
                    },
                },
            },
            series: [this._toHighChartSerie()],
        };
    }
    ngOnChanges({ name, innerSize, showLegend, showDataLabels, data }) {
        if (this.isChartReady) {
            if (data) {
                // update the series
                this.chart.get(this._id).setData(this._toHighChartSerieData(data.currentValue), true, true);
            }
            if (name) {
                this._updatePieSerie({ name: name.currentValue || '' });
            }
            if (innerSize) {
                this._updatePieSerie({ innerSize: innerSize.currentValue }, true);
            }
            if (showLegend) {
                this.chart.legend.update({ enabled: showLegend.currentValue }, true);
            }
            if (showDataLabels) {
                this.chart.update({
                    plotOptions: {
                        pie: { dataLabels: { enabled: showDataLabels.currentValue } },
                    },
                }, true);
            }
        }
    }
    ngAfterContentInit() {
        this.legendFormatters.changes.pipe(map((legend) => legend.length > 0), startWith(this.legendFormatters.length > 0), takeUntil(this.componentDestroyed$)).subscribe(hasLegend => {
            if (hasLegend) {
                this.legendSync(this.legendFormatters.first, this.componentDestroyed$);
            }
            else {
                this.disableLegend();
            }
        });
        this.tooltipFormatters.changes.pipe(map((tooltip) => tooltip.length > 0), startWith(this.tooltipFormatters.length > 0), takeUntil(this.componentDestroyed$)).subscribe(hasTooltip => {
            if (hasTooltip) {
                this.tooltipSync(this.tooltipFormatters.first, this.componentDestroyed$);
            }
            else {
                this.disableTooltip();
            }
        });
        // create the chart
        const chartSettings = Object.assign(this.chartOptions, this._pieChartOptions);
        this.chart = HighCharts.chart(this.chartContainer.nativeElement, chartSettings, this.onChartReady.bind(this));
    }
    ngOnDestroy() {
        if (this.chart) {
            this.chart.destroy();
        }
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }
    _updatePieSerie(data, redraw = false) {
        this.chart.get(this._id).update(data, redraw);
    }
    _toHighChartSerie() {
        return {
            id: this._id,
            type: 'pie',
            point: {
                events: {
                    legendItemClick: (event) => {
                        const datum = this.data.find(d => d.name === event.target.name);
                        this.dataClicked.emit(datum);
                        return this.selectionEnabled;
                    },
                },
            },
            name: this.name,
            innerSize: this.innerSize,
            data: this._toHighChartSerieData(this.data),
        };
    }
    _toHighChartSerieData(data) {
        return (data?.map((item) => ({
            name: item.name,
            y: item.value,
            color: item.color,
            sliced: item.sliced,
            selected: item.selected,
        })) || []);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: PieComponent, deps: [{ token: ChartThemeAdapterService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.1", type: PieComponent, selector: "avn-pie", inputs: { title: "title", subtitle: "subtitle", titlePosition: "titlePosition", showDataLabels: "showDataLabels", innerSize: "innerSize", data: "data", name: "name", selectionEnabled: "selectionEnabled", valueSuffix: "valueSuffix" }, outputs: { dataClicked: "dataClicked" }, queries: [{ propertyName: "tooltipFormatters", predicate: ChartTooltipDirective }, { propertyName: "legendFormatters", predicate: ChartLegendDirective }], viewQueries: [{ propertyName: "chartContainer", first: true, predicate: ["chartContainer"], descendants: true, static: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div #chartContainer (avnResize)=\"onResize()\" class=\"chart-container\"></div>\n", styles: [":host{display:block;width:100%;height:100%}:host .chart-container{width:100%;height:100%}\n"], dependencies: [{ kind: "directive", type: i2.ResizeDirective, selector: "[avnResize]", inputs: ["debounceTime", "emitInitialEvent"], outputs: ["avnResize"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: PieComponent, decorators: [{
            type: Component,
            args: [{ selector: 'avn-pie', inputs: ['title', 'subtitle', 'titlePosition'], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div #chartContainer (avnResize)=\"onResize()\" class=\"chart-container\"></div>\n", styles: [":host{display:block;width:100%;height:100%}:host .chart-container{width:100%;height:100%}\n"] }]
        }], ctorParameters: function () { return [{ type: ChartThemeAdapterService }]; }, propDecorators: { chartContainer: [{
                type: ViewChild,
                args: ['chartContainer', { static: true }]
            }], tooltipFormatters: [{
                type: ContentChildren,
                args: [ChartTooltipDirective]
            }], legendFormatters: [{
                type: ContentChildren,
                args: [ChartLegendDirective]
            }], showDataLabels: [{
                type: Input
            }], innerSize: [{
                type: Input
            }], data: [{
                type: Input
            }], name: [{
                type: Input
            }], selectionEnabled: [{
                type: Input
            }], valueSuffix: [{
                type: Input
            }], dataClicked: [{
                type: Output
            }] } });

class PieModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: PieModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.1", ngImport: i0, type: PieModule, declarations: [PieComponent], imports: [CommonModule, CoreModule], exports: [PieComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: PieModule, imports: [CommonModule, CoreModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: PieModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [PieComponent],
                    imports: [CommonModule, CoreModule],
                    exports: [PieComponent],
                }]
        }] });

// note: constructors are invoked left to right.
// the nested one is called first.
const _ChartBase$2 = mixinChartTheming(mixinChartBase(PieChart));
class DonutComponent extends _ChartBase$2 {
    set datumShownInChartCenter(value) {
        this.datumShownInChartCenterSub.next(value);
    }
    constructor(_chartThemeAdapterService, renderer) {
        super(_chartThemeAdapterService);
        this.renderer = renderer;
        this.precision = 0;
        this.showName = true;
        this._innerSize = '85%';
        /** Emit the name of the data when its serie or its legend is clicked */
        this.dataClicked = new EventEmitter();
        this.componentDestroyed$ = new Subject();
        this._id = v4();
        this.datumShownInChartCenterSub = new BehaviorSubject(null);
        this.valueShownInChartCenter$ = this.datumShownInChartCenterSub.pipe(filter$1((datum) => Object.keys(datum || {}).length > 0), map((datum) => {
            if (this.chart) {
                this.chart.redraw();
            }
            return this.precision != null ? +datum.value.toFixed(this.precision) : datum.value;
        }));
    }
    ngOnInit() {
        this._chartThemeAdapterService.chartTheme$.pipe(takeUntil(this.componentDestroyed$)).subscribe((r) => {
            this.chart?.update(r, true);
        });
        // set the initial options and pie serie
        // we dont wanna expose the title to the user as it made up of the current value
        // TODO maybe keep the title and position the value in the middle with css directly (note: using highchart title and center it is not so great with a small chart...)
        this._pieChartOptions = {
            title: {
                text: this.title,
                verticalAlign: 'top',
                y: 25,
            },
            credits: { enabled: false },
            chart: {
                type: 'pie',
                margin: [0, 0, 0, 0],
                spacingTop: 0,
                spacingRight: 0,
                spacingBottom: 0,
                spacingLeft: 0,
                plotBorderWidth: 0,
                events: {
                    redraw: () => {
                        const valueInChartCenter = this.datumShownInChartCenterSub?.value?.value || 0;
                        this.renderer.setStyle(this.titleEl.nativeElement, 'fontSize', `${Math.min(this.chart.chartHeight, this.chart.chartWidth) / (valueInChartCenter ? 6 : 4)}px`);
                    },
                },
            },
            accessibility: { point: { valueSuffix: this.valueSuffix } },
            legend: { enabled: false },
            tooltip: { enabled: false },
            plotOptions: {
                pie: {
                    size: '100%',
                    slicedOffset: this.title ? 40 : 0,
                    borderWidth: 0,
                    allowPointSelect: false,
                    dataLabels: { enabled: this.showDataLabels },
                    showInLegend: false,
                    innerSize: this._innerSize,
                },
                series: {
                    events: {
                        click: (event) => {
                            const datum = this.data.find(d => d.name === event.point.name);
                            this.dataClicked.emit(datum);
                            this.datumShownInChartCenterSub.next(datum);
                        },
                    },
                    states: {
                        hover: {
                            enabled: true,
                            halo: null,
                        },
                    },
                },
            },
            series: [this._toHighChartSerie()],
        };
    }
    ngOnChanges({ data, precision, valueSuffix, showDataLabels }) {
        if (this.isChartReady) {
            if (data || precision) {
                // update the series
                this.chart.get(this._id).setData(this._toHighChartSerieData(), true, true);
            }
            if (valueSuffix) {
                this.chart.update({
                    tooltip: {
                        pointFormat: `{series.name}: <b>{point.percentage:.1f}${this.valueSuffix}</b>`,
                    },
                }, true);
            }
            if (showDataLabels) {
                this.chart.update({
                    plotOptions: {
                        pie: { dataLabels: { enabled: showDataLabels.currentValue } },
                    },
                }, true);
            }
        }
    }
    ngAfterContentInit() {
        // create the chart
        const chartSettings = Object.assign(this.chartOptions, this._pieChartOptions);
        this.chart = HighCharts.chart(this.chartContainer.nativeElement, chartSettings, this.onChartReady.bind(this));
    }
    ngOnDestroy() {
        if (this.chart) {
            this.chart.destroy();
        }
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }
    _updatePieSerie(data, redraw = false) {
        this.chart.get(this._id).update(data, redraw);
    }
    getDisplayValue() {
        return this.datumShownInChartCenterSub.value !== null ?
            (this.precision != null ? +this.datumShownInChartCenterSub.value.value.toFixed(this.precision) : this.datumShownInChartCenterSub.value.value) :
            undefined;
    }
    _toHighChartSerie() {
        return {
            id: this._id,
            type: 'pie',
            data: this._toHighChartSerieData(),
        };
    }
    _toHighChartSerieData() {
        const displayValue = (value) => {
            const rawValue = value || 0;
            return this.precision != null ? +rawValue.toFixed(this.precision) : rawValue;
        };
        if (!(this.data || []).length) {
            return [{
                    id: 'dummy',
                    y: 100,
                    color: 'gray',
                    opacity: 0.3,
                    selected: false,
                    sliced: false,
                }];
        }
        const seriesData = this.data.map((datum) => {
            return {
                name: datum.name,
                y: displayValue(datum.value),
                color: datum.color || 'gray',
                opacity: datum.color ? 1 : 0.3,
                custom: { suffix: this.valueSuffix },
            };
        });
        return seriesData;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: DonutComponent, deps: [{ token: ChartThemeAdapterService }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.1", type: DonutComponent, selector: "avn-donut", inputs: { title: "title", subtitle: "subtitle", data: "data", precision: "precision", valueSuffix: "valueSuffix", showDataLabels: "showDataLabels", showName: "showName", datumShownInChartCenter: "datumShownInChartCenter" }, outputs: { dataClicked: "dataClicked" }, viewQueries: [{ propertyName: "chartContainer", first: true, predicate: ["chartContainer"], descendants: true, static: true }, { propertyName: "titleEl", first: true, predicate: ["title"], descendants: true, static: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div #chartContainer (avnResize)=\"onResize()\" class=\"chart-container\"></div>\n\n<div\n  #title\n  class=\"title\"\n  [style.color]=\"(datumShownInChartCenterSub | async)?.color\">\n  <ng-container *ngIf=\"(valueShownInChartCenter$ | async) as valueShownInChartCenter\">\n    <span>{{ valueShownInChartCenter }}</span><span>{{ valueSuffix }}</span>\n  </ng-container>\n</div>\n", styles: [":host{display:block;width:100%;height:100%;position:relative}:host .chart-container{width:100%;height:100%;position:relative}:host .title{width:-moz-fit-content;width:fit-content;height:-moz-fit-content;height:fit-content;position:absolute;inset:0;margin:auto}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.ResizeDirective, selector: "[avnResize]", inputs: ["debounceTime", "emitInitialEvent"], outputs: ["avnResize"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: DonutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'avn-donut', inputs: ['title', 'subtitle'], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div #chartContainer (avnResize)=\"onResize()\" class=\"chart-container\"></div>\n\n<div\n  #title\n  class=\"title\"\n  [style.color]=\"(datumShownInChartCenterSub | async)?.color\">\n  <ng-container *ngIf=\"(valueShownInChartCenter$ | async) as valueShownInChartCenter\">\n    <span>{{ valueShownInChartCenter }}</span><span>{{ valueSuffix }}</span>\n  </ng-container>\n</div>\n", styles: [":host{display:block;width:100%;height:100%;position:relative}:host .chart-container{width:100%;height:100%;position:relative}:host .title{width:-moz-fit-content;width:fit-content;height:-moz-fit-content;height:fit-content;position:absolute;inset:0;margin:auto}\n"] }]
        }], ctorParameters: function () { return [{ type: ChartThemeAdapterService }, { type: i0.Renderer2 }]; }, propDecorators: { chartContainer: [{
                type: ViewChild,
                args: ['chartContainer', { static: true }]
            }], titleEl: [{
                type: ViewChild,
                args: ['title', { static: true }]
            }], data: [{
                type: Input
            }], precision: [{
                type: Input
            }], valueSuffix: [{
                type: Input
            }], showDataLabels: [{
                type: Input
            }], showName: [{
                type: Input
            }], datumShownInChartCenter: [{
                type: Input
            }], dataClicked: [{
                type: Output
            }] } });

class DonutModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: DonutModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.1", ngImport: i0, type: DonutModule, declarations: [DonutComponent], imports: [CommonModule, CoreModule], exports: [DonutComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: DonutModule, imports: [CommonModule, CoreModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: DonutModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [DonutComponent],
                    imports: [CommonModule, CoreModule],
                    exports: [DonutComponent],
                }]
        }] });

// note: constructors are invoked left to right.
// the nested one is called first.
const _ChartBase$1 = mixinChartTheming(mixinChartBase(PieChart));
class PercentDonutComponent extends _ChartBase$1 {
    constructor(_chartThemeAdapterService, renderer) {
        super(_chartThemeAdapterService);
        this.renderer = renderer;
        this.total = 100;
        this.precision = 0;
        this.showName = true;
        this._innerSize = '85%';
        this.componentDestroyed$ = new Subject();
        this._id = v4();
    }
    ngOnInit() {
        this._chartThemeAdapterService.chartTheme$.pipe(takeUntil(this.componentDestroyed$)).subscribe((r) => {
            this.chart?.update(r, true);
        });
        // set the initial options and pie serie
        // we dont wanna expose the title to the user as it made up of the current value
        // TODO maybe keep the title and position the value in the middle with css directly (note: using highchart title and center it is not so great with a small chart...)
        this._pieChartOptions = {
            title: {
                text: this.name ? this.name : '',
                verticalAlign: 'top',
                y: 25,
            },
            // subtitle: this.showName ? {
            //   text: this.name,
            //   floating: true,
            //   align: 'center',
            //   verticalAlign: 'middle',
            // } : {},
            credits: { enabled: false },
            chart: {
                type: 'pie',
                margin: [0, 0, 0, 0],
                spacingTop: 0,
                spacingRight: 0,
                spacingBottom: 0,
                spacingLeft: 0,
                plotBorderWidth: 0,
                events: {
                    redraw: () => {
                        this.renderer.setStyle(this.titleEl.nativeElement, 'fontSize', `${Math.min(this.chart.chartHeight, this.chart.chartWidth) / (this.value ? 6 : 4)}px`);
                        // this.chart.title.update({
                        //   style: {
                        //     fontSize: `${Math.min(this.chart.chartHeight, this.chart.chartWidth)/4}px`
                        //   },
                        // }, false);
                        // this.chart.update({style : { fontSize: }}).fontSize = '5em';
                        // chart.container.style.fontSize = this.value + 'em';
                        //
                        // // Update layout based on new font and line sizes
                        // chart.isDirtyLegend = true;
                        // chart.redraw(false);
                    },
                },
            },
            accessibility: { point: { valueSuffix: this.valueSuffix } },
            legend: { enabled: false },
            tooltip: {
                pointFormat: `{series.name}: <b>{point.percentage:.1f}${this.valueSuffix}</b>`,
                formatter() {
                    if (this.point.id !== 'dummy') {
                        return this.y + `${this.point.custom.suffix}`;
                    }
                    else {
                        return false;
                    }
                },
                enabled: false,
            },
            plotOptions: {
                pie: {
                    size: '100%',
                    slicedOffset: this.name ? 40 : 0,
                    borderWidth: 0,
                    allowPointSelect: false,
                    dataLabels: {
                        enabled: false,
                    },
                    showInLegend: false,
                    innerSize: this._innerSize,
                },
                series: {
                    states: {
                        hover: {
                            enabled: false,
                        },
                        inactive: {
                            enabled: false,
                        },
                    },
                },
            },
            series: [this._toHighChartSerie()],
        };
        // create the chart
        const chartSettings = Object.assign(this.chartOptions, this._pieChartOptions);
        this.chart = HighCharts.chart(this.chartContainer.nativeElement, chartSettings, this.onChartReady.bind(this));
    }
    ngOnChanges({ name, value, precision, color, showLegend, showDataLabels, valueSuffix }) {
        if (this.isChartReady) {
            if (name || value || precision || color) {
                // if (name) { // TODO we are now setting the text in the middle of the donut via css (highcharts does not center align well on
                // small charts...)
                //   // update the subtitle
                //   this.subtitle = this.name;
                // }
                // update the series
                this.chart.get(this._id).setData(this._toHighChartSerieData(), true, true);
            }
            if (name) {
                this.title = name.currentValue;
                this._updatePieSerie({ name: name.currentValue || '' });
            }
            if (showLegend) {
                this.chart.legend.update({ enabled: showLegend.currentValue }, true);
            }
            if (showDataLabels) {
                this.chart.update({
                    plotOptions: {
                        pie: { dataLabels: { enabled: showDataLabels.currentValue } },
                    },
                }, true);
            }
            if (valueSuffix) {
                this.chart.update({
                    tooltip: {
                        pointFormat: `{series.name}: <b>{point.percentage:.1f}${this.valueSuffix}</b>`,
                    },
                }, true);
            }
        }
    }
    _updatePieSerie(data, redraw = false) {
        this.chart.get(this._id).update(data, redraw);
    }
    getDisplayValue() {
        return this.value ? (this.precision != null ? +this.value.toFixed(this.precision) : this.value) : 0;
    }
    _toHighChartSerie() {
        return {
            id: this._id,
            type: 'pie',
            name: this.name,
            data: this._toHighChartSerieData(),
        };
    }
    _toHighChartSerieData() {
        const dummy = {
            id: 'dummy',
            y: this.total - this.getDisplayValue(),
            color: this.color,
            opacity: 0.3,
            selected: false,
            sliced: false,
        };
        if (!this.value) {
            return [dummy];
        }
        return [
            {
                name: this.name,
                y: this.getDisplayValue(),
                color: this.color,
                custom: { suffix: this.valueSuffix },
            },
            dummy,
        ];
    }
    ngOnDestroy() {
        if (this.chart) {
            this.chart.destroy();
        }
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: PercentDonutComponent, deps: [{ token: ChartThemeAdapterService }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.1", type: PercentDonutComponent, selector: "avn-percent-donut", inputs: { total: "total", name: "name", value: "value", precision: "precision", color: "color", valueSuffix: "valueSuffix", showName: "showName" }, viewQueries: [{ propertyName: "chartContainer", first: true, predicate: ["chartContainer"], descendants: true, static: true }, { propertyName: "titleEl", first: true, predicate: ["title"], descendants: true, static: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div #chartContainer (avnResize)=\"onResize()\" class=\"chart-container\"></div>\n<div class=\"title\" #title>\n  {{ getDisplayValue() }}{{ valueSuffix }}\n  <!--  <ng-container *ngIf=\"showName\">-->\n  <!--    <br>{{name}}-->\n  <!--  </ng-container>-->\n</div>\n", styles: [":host{display:block;width:100%;height:100%;position:relative}:host .chart-container{width:100%;height:100%;position:relative}:host .title{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;margin:0}\n"], dependencies: [{ kind: "directive", type: i2.ResizeDirective, selector: "[avnResize]", inputs: ["debounceTime", "emitInitialEvent"], outputs: ["avnResize"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: PercentDonutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'avn-percent-donut', inputs: [], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div #chartContainer (avnResize)=\"onResize()\" class=\"chart-container\"></div>\n<div class=\"title\" #title>\n  {{ getDisplayValue() }}{{ valueSuffix }}\n  <!--  <ng-container *ngIf=\"showName\">-->\n  <!--    <br>{{name}}-->\n  <!--  </ng-container>-->\n</div>\n", styles: [":host{display:block;width:100%;height:100%;position:relative}:host .chart-container{width:100%;height:100%;position:relative}:host .title{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;margin:0}\n"] }]
        }], ctorParameters: function () { return [{ type: ChartThemeAdapterService }, { type: i0.Renderer2 }]; }, propDecorators: { chartContainer: [{
                type: ViewChild,
                args: ['chartContainer', { static: true }]
            }], titleEl: [{
                type: ViewChild,
                args: ['title', { static: true }]
            }], total: [{
                type: Input
            }], name: [{
                type: Input
            }], value: [{
                type: Input
            }], precision: [{
                type: Input
            }], color: [{
                type: Input
            }], valueSuffix: [{
                type: Input
            }], showName: [{
                type: Input
            }] } });

class PercentDonutModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: PercentDonutModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.1", ngImport: i0, type: PercentDonutModule, declarations: [PercentDonutComponent], imports: [CommonModule, CoreModule], exports: [PercentDonutComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: PercentDonutModule, imports: [CommonModule, CoreModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: PercentDonutModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [PercentDonutComponent],
                    imports: [CommonModule, CoreModule],
                    exports: [PercentDonutComponent],
                }]
        }] });

// note: constructors are invoked left to right.
// the nested one is called first.
const _ChartBase = mixinChartTheming(mixinChartBase(PieChart));
/**
 * - Stops
 * Tried to use yAxis.stops to determine the color threshold, however this needs a rgb or hex value, wont work with css vars
 * Also its auto modifying the color (workaround here https://www.highcharts.com/forum/viewtopic.php?t=40568)
 * => update the color on value change instead
 *
 */
class SolidGaugeComponent extends _ChartBase {
    constructor(_chartThemeAdapterService, renderer) {
        super(_chartThemeAdapterService);
        this.renderer = renderer;
        this.min = 0;
        this.max = 100;
        this.showName = true;
        this.textSize = true;
        this.layout = 'full'; // TODO half is not completed. still got some align issue
        this.componentDestroyed$ = new Subject();
        this._id = v4();
    }
    ngOnInit() {
        this._chartThemeAdapterService.chartTheme$.pipe(takeUntil(this.componentDestroyed$)).subscribe((r) => {
            this.chart?.update(r, true);
        });
        // set the initial options and pie serie
        // we dont wanna expose the title to the user as it made up of the current value
        // TODO maybe keep the title and position the value in the middle with css directly (note: using highchart title and center it is not so great with a small chart...)
        this._pieChartOptions = {
            chart: {
                animation: false,
                type: 'solidgauge',
                margin: [0, 0, 0, 0],
                spacingTop: 0,
                spacingRight: 0,
                spacingBottom: 0,
                spacingLeft: 0,
                plotBorderWidth: 0,
                // events: {
                //   redraw: () => {
                //     // TODO title does not get recentered properly...
                //     if (this.chart && this.chart['lastWidth'] !== this.chart.plotWidth || this.chart['lastValueSuffix'] !== this.valueSuffix) {
                //       this.chart['lastWidth'] = this.chart.plotWidth;
                //       this.chart['lastValueSuffix'] = this.valueSuffix;
                //       // @ts-ignore
                //       this.chart.get(this._id).update({
                //         dataLabels: {
                //           formatter: this.formatterFunction.bind(this)
                //         }
                //       });
                //     }
                //   }
                // }
            },
            pane: {
                size: '100%',
                startAngle: this.layout === 'half' ? -90 : 0,
                endAngle: this.layout === 'half' ? 90 : 360,
                background: [
                    {
                        ...this._getPaneOptions(),
                        innerRadius: '80%',
                    },
                ],
            },
            yAxis: {
                // ...this._toColorStops(this.colorThresholds),
                stops: [[1, this.color || HighCharts.getOptions().colors[0]]],
                lineWidth: 0,
                tickWidth: 0,
                minorTickInterval: null,
                tickAmount: 0,
                labels: {
                    enabled: false, // hide the min value
                },
                min: this.min,
                max: this.max,
                minorTicks: false,
            },
            accessibility: { point: { valueSuffix: this.valueSuffix } },
            tooltip: {
                enabled: false,
            },
            plotOptions: {
                solidgauge: {
                    borderWidth: 0,
                    allowPointSelect: false,
                    dataLabels: {
                        // enabled: false,
                        y: 0,
                        borderWidth: 0,
                        useHTML: true,
                    },
                    showInLegend: false,
                },
                series: {
                    states: {
                        hover: {
                            enabled: false,
                        },
                        inactive: {
                            enabled: false,
                        },
                    },
                },
            },
            series: [this._toHighChartSerie('80%')],
            responsive: {
                rules: [
                    {
                        condition: {
                            callback: () => this.chart?.chartWidth <= 100 || this.chart?.chartHeight <= 100,
                        },
                        chartOptions: {
                            yAxis: {
                                title: {
                                    y: -70,
                                },
                            },
                            pane: {
                                background: [
                                    {
                                        ...this._getPaneOptions(),
                                        innerRadius: '85%',
                                    },
                                ],
                            },
                            series: [this._toHighChartSerie('85%')],
                        },
                    },
                    {
                        condition: {
                            callback: () => this.chart?.chartWidth > 100 && this.chart.chartHeight > 100,
                        },
                        chartOptions: {
                            yAxis: {
                                title: {
                                    y: -70,
                                },
                            },
                            pane: {
                                background: [
                                    {
                                        ...this._getPaneOptions(),
                                        innerRadius: '80%',
                                    },
                                ],
                            },
                            series: [this._toHighChartSerie('80%')],
                        },
                    },
                ],
            },
        };
        // create the chart
        const chartSettings = Object.assign(this.chartOptions, this._pieChartOptions);
        this.chart = HighCharts.chart(this.chartContainer.nativeElement, chartSettings, this.onChartReady.bind(this));
        // TODO uncomment to enable title (need to adjust position)
        // this.title = this.name;
        // this.titlePosition = {
        //   horizontal: 'center',
        //   vertical: 'bottom'
        // };
    }
    _getPaneOptions() {
        return {
            shape: 'arc',
            borderWidth: 0,
            outerRadius: '100%',
            backgroundColor: 'rgba(235,235,235,0.2)',
        };
    }
    formatterFunction() {
        if (!this.chart) {
            return '';
        }
        const fontSize = Math.max(Math.min(this.chart.plotWidth, this.chart.plotHeight) / 80, 0.8) + 'rem !important';
        return `<span style="font-size:${fontSize}">${this.value}${this.valueSuffix || ''}</span>`;
    }
    ngOnChanges({ name, value, color, valueSuffix }) {
        if (this.isChartReady) {
            if (name) {
                this._updateSerie({ name: name.currentValue || '' });
            }
            if (value) {
                this._updateSerie({ value: [value.currentValue || 0] }, true);
            }
            if (color) {
                this.chart.update({
                    yAxis: {
                        stops: [[1, this.color || HighCharts.getOptions().colors[0]]],
                    },
                }, true);
            }
            if (valueSuffix) {
                // this.title = !isNaN(this.value) ? this.value + this.valueSuffix : '';
                // this.chart.update(   { tooltip: { pointFormat: `{series.name}: <b>{point.percentage:.1f}${this.valueSuffix}</b>` }}, true);
                // only need a chart redraw
                this.chart.redraw(false);
            }
        }
    }
    _updateSerie(data, redraw = false) {
        this.chart.get(this._id).update(data, redraw);
    }
    _toHighChartSerie(innerRadius) {
        return {
            id: this._id,
            type: 'solidgauge',
            name: this.name,
            data: [this.value],
            innerRadius,
            dataLabels: {
                align: 'center',
                verticalAlign: 'middle',
                formatter: this.formatterFunction.bind(this),
            },
            animation: true,
        };
    }
    ngOnDestroy() {
        if (this.chart) {
            this.chart.destroy();
        }
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SolidGaugeComponent, deps: [{ token: ChartThemeAdapterService }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.1", type: SolidGaugeComponent, selector: "avn-solid-gauge", inputs: { title: "title", subtitle: "subtitle", min: "min", max: "max", name: "name", value: "value", color: "color", valueSuffix: "valueSuffix", showName: "showName", textSize: "textSize", layout: "layout" }, viewQueries: [{ propertyName: "chartContainer", first: true, predicate: ["chartContainer"], descendants: true, static: true }, { propertyName: "titleEl", first: true, predicate: ["title"], descendants: true, static: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div #chartContainer (avnResize)=\"onResize()\" class=\"chart-container\"></div>\n<!--<div class=\"title\" #title>-->\n<!--  {{value}}{{valueSuffix}}-->\n<!--&lt;!&ndash;  <ng-container *ngIf=\"showName\">&ndash;&gt;-->\n<!--&lt;!&ndash;    <br>{{name}}&ndash;&gt;-->\n<!--&lt;!&ndash;  </ng-container>&ndash;&gt;-->\n<!--</div>-->\n", styles: [":host{display:block;width:100%;height:100%;position:relative}:host .chart-container{width:100%;height:100%;position:relative}:host ::ng-deep .highcharts-data-labels{inset:0}:host ::ng-deep .highcharts-data-labels .highcharts-data-label{position:relative!important;display:flex;height:100%;align-items:center;justify-content:center;width:100%;left:unset!important;top:unset!important}:host ::ng-deep .highcharts-data-labels .highcharts-data-label>span{position:relative!important;font-size:unset!important;top:0!important;left:0!important;line-height:0px;color:inherit!important}\n"], dependencies: [{ kind: "directive", type: i2.ResizeDirective, selector: "[avnResize]", inputs: ["debounceTime", "emitInitialEvent"], outputs: ["avnResize"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SolidGaugeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'avn-solid-gauge', inputs: ['title', 'subtitle'], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div #chartContainer (avnResize)=\"onResize()\" class=\"chart-container\"></div>\n<!--<div class=\"title\" #title>-->\n<!--  {{value}}{{valueSuffix}}-->\n<!--&lt;!&ndash;  <ng-container *ngIf=\"showName\">&ndash;&gt;-->\n<!--&lt;!&ndash;    <br>{{name}}&ndash;&gt;-->\n<!--&lt;!&ndash;  </ng-container>&ndash;&gt;-->\n<!--</div>-->\n", styles: [":host{display:block;width:100%;height:100%;position:relative}:host .chart-container{width:100%;height:100%;position:relative}:host ::ng-deep .highcharts-data-labels{inset:0}:host ::ng-deep .highcharts-data-labels .highcharts-data-label{position:relative!important;display:flex;height:100%;align-items:center;justify-content:center;width:100%;left:unset!important;top:unset!important}:host ::ng-deep .highcharts-data-labels .highcharts-data-label>span{position:relative!important;font-size:unset!important;top:0!important;left:0!important;line-height:0px;color:inherit!important}\n"] }]
        }], ctorParameters: function () { return [{ type: ChartThemeAdapterService }, { type: i0.Renderer2 }]; }, propDecorators: { chartContainer: [{
                type: ViewChild,
                args: ['chartContainer', { static: true }]
            }], titleEl: [{
                type: ViewChild,
                args: ['title', { static: true }]
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }], name: [{
                type: Input
            }], value: [{
                type: Input
            }], color: [{
                type: Input
            }], valueSuffix: [{
                type: Input
            }], showName: [{
                type: Input
            }], textSize: [{
                type: Input
            }], layout: [{
                type: Input
            }] } });

class SolidGaugeModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SolidGaugeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.1", ngImport: i0, type: SolidGaugeModule, declarations: [SolidGaugeComponent], imports: [CommonModule, CoreModule], exports: [SolidGaugeComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SolidGaugeModule, imports: [CommonModule, CoreModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: SolidGaugeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SolidGaugeComponent],
                    imports: [CommonModule, CoreModule],
                    exports: [SolidGaugeComponent],
                }]
        }] });

var CountCardValueSize;
(function (CountCardValueSize) {
    CountCardValueSize[CountCardValueSize["default"] = 1] = "default";
    CountCardValueSize[CountCardValueSize["lg"] = 2] = "lg";
    CountCardValueSize[CountCardValueSize["md"] = 3] = "md";
    CountCardValueSize[CountCardValueSize["sm"] = 4] = "sm";
})(CountCardValueSize || (CountCardValueSize = {}));

class CountCardComponent {
    /**
     * change cursor to pointer, if click is bind by parent
     */
    get addClickableClass() {
        return this.click.observers.length > 0;
    }
    constructor(cdr) {
        this.cdr = cdr;
        /**
         * Theme of the Card
         */
        this.theme = 'primary';
        /**
         * Define position of the value
         */
        this.valuePosition = 'left';
        /**
         * Define the type of font-size
         */
        this.valueSize = CountCardValueSize.default;
        /**
         * Used only for binding css class
         */
        // eslint-disable-next-line @angular-eslint/no-output-native
        this.click = new EventEmitter();
        /**
         * Indicates if the icon is disabled.
         * When disabled it will have a lighter opacity and will not emit the action when clicked
         */
        this.disabled = false;
        this.svgTextPostionX = 0;
        this.icons = {
            success: 'action:check_circle',
            warning: 'alert:warning',
            danger: 'navigation:cancel',
        };
    }
    ngAfterViewInit() {
        if (this.icon) {
            this.icons = { ...this.icons, ...{ [this.theme]: this.icon } };
        }
        this.cdr.detectChanges();
    }
    sizeSvgChange() {
        // the min height have to be equal to the width
        this.cardMinHeight = this.mainContainer.nativeElement.clientWidth;
        // change viewBox width on resize
        const svgTextWidth = this.svgSize.nativeElement.clientWidth > 150 * this.valueSize ? this.svgSize.nativeElement.clientWidth : 150 * this.valueSize;
        this.svgValue = `0 0 ${svgTextWidth} 75`;
        this.svgTextPostionX = this.valuePosition === 'right' ? svgTextWidth : 0;
        // resize icon of the card
        this.iconSize =
            this.mainContainer.nativeElement.clientWidth > this.mainContainer.nativeElement.clientHeight
                ? this.mainContainer.nativeElement.clientHeight
                : this.mainContainer.nativeElement.clientWidth;
        // Add class when small card
        this.smallCard = this.mainContainer.nativeElement.clientWidth < 175 ? true : false;
        // display the value of the card after resizing
        this.showValue = true;
        this.cdr.detectChanges();
    }
    onClick(event) {
        if (this.disabled) {
            event.stopPropagation();
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: CountCardComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.1", type: CountCardComponent, selector: "avn-count-card", inputs: { title: "title", theme: "theme", footerText: "footerText", value: "value", uom: "uom", footerTemplate: "footerTemplate", removeIcon: "removeIcon", icon: "icon", valuePosition: "valuePosition", valueSize: "valueSize", disabled: "disabled" }, outputs: { click: "click" }, host: { properties: { "class.disabled": "this.disabled", "class.clickable-card": "this.addClickableClass" } }, viewQueries: [{ propertyName: "mainContainer", first: true, predicate: ["mainContainer"], descendants: true, static: true }, { propertyName: "svgSize", first: true, predicate: ["svgSize"], descendants: true, static: true }], ngImport: i0, template: "<div #mainContainer [ngClass]=\"['main-container theme-' + theme, showValue ? 'show ' : '']\" (click)=\"onClick($event)\">\n  <div [ngClass]=\"'container'\" [style.min-height.px]=\"cardMinHeight\">\n    <div class=\"sub-container\" [style.padding]=\"smallCard ? 'var(--space-sm)' : 'var(--space-base)'\">\n      <div class=\"header\">\n        <h5>{{ title }}</h5>\n      </div>\n      <div class=\"body\">\n        <div class=\"count-container\">\n          <div class=\"sub-count-container\">\n            <div [ngClass]=\"'svg-container'\">\n              <svg\n                width=\"100%\"\n                height=\"100%\"\n                [attr.viewBox]=\"svgValue\"\n                preserveAspectRatio=\"xMinYMid meet\"\n                xmlns=\"http://www.w3.org/2000/svg\"\n                xmlns:xlink=\"http://www.w3.org/1999/xlink\"\n              >\n                <text\n                  class=\"textValue\"\n                  [attr.text-anchor]=\"valuePosition === 'right' ? 'end' : 'start'\"\n                  [attr.transform]=\"'translate(' + svgTextPostionX + ')'\"\n                  y=\"65\"\n                  font-size=\"75px\"\n                  [attr.fill]=\"'var(--' + theme + '-on)'\"\n                >\n                  {{ value }}\n                  <tspan *ngIf=\"uom\" font-size=\"35px\">{{ uom }}</tspan>\n                </text>\n              </svg>\n            </div>\n          </div>\n        </div>\n        <avn-icon *ngIf=\"!removeIcon\" [class]=\"theme ? 'hasTheme' : null\" [icon]=\"icons[theme]\" [fontSize]=\"iconSize / 1.5\"></avn-icon>\n      </div>\n      <div class=\"footer\">\n        <hr\n          *ngIf=\"footerText\"\n          class=\"separator\"\n          [style.margin-top]=\"smallCard ? 'var(--space-sm)' : 'var(--space-base)'\"\n          [style.margin-bottom]=\"smallCard ? 'var(--space-sm)' : 'var(--space-base)'\"\n        />\n        <ng-container *ngIf=\"!!footerTemplate; else text\">\n          <ng-container *ngTemplateOutlet=\"footerTemplate; context: { name: footerText }\"></ng-container>\n        </ng-container>\n        <ng-template #text>\n          <p class=\"label\">{{ footerText }}</p>\n        </ng-template>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div class=\"svg-size\">\n  <svg\n    (avnResize)=\"sizeSvgChange()\"\n    [debounceTime]=\"0\"\n    width=\"100%\"\n    height=\"100%\"\n    [attr.viewBox]=\"svgValue\"\n    preserveAspectRatio=\"xMinYMid meet\"\n    xmlns=\"http://www.w3.org/2000/svg\"\n    xmlns:xlink=\"http://www.w3.org/1999/xlink\"\n  >\n    <text #svgSize y=\"65\" font-size=\"75px\">\n      {{ value }}\n      <tspan *ngIf=\"uom\" font-size=\"35px\">{{ uom }}</tspan>\n    </text>\n  </svg>\n</div>\n", styles: [":host{width:100%;height:100%}:host .theme-primary{background:var(--primary-base);color:var(--primary-on)}:host.clickable-card .theme-primary:hover:not(.disabled){background:var(--primary-variant);color:var(--primary-variant-on)}:host .theme-secondary{background:var(--secondary-base);color:var(--secondary-on)}:host.clickable-card .theme-secondary:hover:not(.disabled){background:var(--secondary-variant);color:var(--secondary-variant-on)}:host .theme-highlight{background:var(--highlight-base);color:var(--highlight-on)}:host.clickable-card .theme-highlight:hover:not(.disabled){background:var(--highlight-variant);color:var(--highlight-variant-on)}:host .theme-danger{background:var(--danger-base);color:var(--danger-on)}:host.clickable-card .theme-danger:hover:not(.disabled){background:var(--danger-variant);color:var(--danger-variant-on)}:host .theme-warning{background:var(--warning-base);color:var(--warning-on)}:host.clickable-card .theme-warning:hover:not(.disabled){background:var(--warning-variant);color:var(--warning-variant-on)}:host .theme-info{background:var(--info-base);color:var(--info-on)}:host.clickable-card .theme-info:hover:not(.disabled){background:var(--info-variant);color:var(--info-variant-on)}:host .theme-success{background:var(--success-base);color:var(--success-on)}:host.clickable-card .theme-success:hover:not(.disabled){background:var(--success-variant);color:var(--success-variant-on)}:host .main-container{width:100%;height:100%;overflow:hidden;border-radius:3px;opacity:0;transition:opacity .5s linear}:host .main-container.show{opacity:1}:host .container{position:relative;width:100%;height:100%}:host .container .separator{height:0}:host .container .header{flex:2 1 auto}:host .container .body{display:flex;flex-direction:column;align-items:flex-start;justify-content:flex-end;flex:1 0 50%}:host .container .body .count-container{width:100%;height:100%;display:flex;justify-content:flex-end;align-items:center}:host .container .body .count-container .sub-count-container{display:flex;align-items:baseline;width:100%}:host .container .body .count-container .sub-count-container.hide{opacity:0}:host .container .body avn-icon{position:absolute;top:-25%;right:-25%;opacity:.2;z-index:0}:host .container .footer p{margin:var(--space-xs) 0 0 0}:host .container div{z-index:1;width:100%}:host .sub-container{min-height:inherit;display:flex;flex-direction:column;justify-content:space-between;height:100%;width:100%}:host.clickable-card{cursor:pointer}:host .svg-size{position:absolute;width:100%;top:-99999999999px;left:-99999999999px;opacity:0}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.ResizeDirective, selector: "[avnResize]", inputs: ["debounceTime", "emitInitialEvent"], outputs: ["avnResize"] }, { kind: "component", type: i2.IconComponent, selector: "avn-icon", inputs: ["icon", "rotate", "fontSize", "actionId", "disabled"], outputs: ["click"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: CountCardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'avn-count-card', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div #mainContainer [ngClass]=\"['main-container theme-' + theme, showValue ? 'show ' : '']\" (click)=\"onClick($event)\">\n  <div [ngClass]=\"'container'\" [style.min-height.px]=\"cardMinHeight\">\n    <div class=\"sub-container\" [style.padding]=\"smallCard ? 'var(--space-sm)' : 'var(--space-base)'\">\n      <div class=\"header\">\n        <h5>{{ title }}</h5>\n      </div>\n      <div class=\"body\">\n        <div class=\"count-container\">\n          <div class=\"sub-count-container\">\n            <div [ngClass]=\"'svg-container'\">\n              <svg\n                width=\"100%\"\n                height=\"100%\"\n                [attr.viewBox]=\"svgValue\"\n                preserveAspectRatio=\"xMinYMid meet\"\n                xmlns=\"http://www.w3.org/2000/svg\"\n                xmlns:xlink=\"http://www.w3.org/1999/xlink\"\n              >\n                <text\n                  class=\"textValue\"\n                  [attr.text-anchor]=\"valuePosition === 'right' ? 'end' : 'start'\"\n                  [attr.transform]=\"'translate(' + svgTextPostionX + ')'\"\n                  y=\"65\"\n                  font-size=\"75px\"\n                  [attr.fill]=\"'var(--' + theme + '-on)'\"\n                >\n                  {{ value }}\n                  <tspan *ngIf=\"uom\" font-size=\"35px\">{{ uom }}</tspan>\n                </text>\n              </svg>\n            </div>\n          </div>\n        </div>\n        <avn-icon *ngIf=\"!removeIcon\" [class]=\"theme ? 'hasTheme' : null\" [icon]=\"icons[theme]\" [fontSize]=\"iconSize / 1.5\"></avn-icon>\n      </div>\n      <div class=\"footer\">\n        <hr\n          *ngIf=\"footerText\"\n          class=\"separator\"\n          [style.margin-top]=\"smallCard ? 'var(--space-sm)' : 'var(--space-base)'\"\n          [style.margin-bottom]=\"smallCard ? 'var(--space-sm)' : 'var(--space-base)'\"\n        />\n        <ng-container *ngIf=\"!!footerTemplate; else text\">\n          <ng-container *ngTemplateOutlet=\"footerTemplate; context: { name: footerText }\"></ng-container>\n        </ng-container>\n        <ng-template #text>\n          <p class=\"label\">{{ footerText }}</p>\n        </ng-template>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div class=\"svg-size\">\n  <svg\n    (avnResize)=\"sizeSvgChange()\"\n    [debounceTime]=\"0\"\n    width=\"100%\"\n    height=\"100%\"\n    [attr.viewBox]=\"svgValue\"\n    preserveAspectRatio=\"xMinYMid meet\"\n    xmlns=\"http://www.w3.org/2000/svg\"\n    xmlns:xlink=\"http://www.w3.org/1999/xlink\"\n  >\n    <text #svgSize y=\"65\" font-size=\"75px\">\n      {{ value }}\n      <tspan *ngIf=\"uom\" font-size=\"35px\">{{ uom }}</tspan>\n    </text>\n  </svg>\n</div>\n", styles: [":host{width:100%;height:100%}:host .theme-primary{background:var(--primary-base);color:var(--primary-on)}:host.clickable-card .theme-primary:hover:not(.disabled){background:var(--primary-variant);color:var(--primary-variant-on)}:host .theme-secondary{background:var(--secondary-base);color:var(--secondary-on)}:host.clickable-card .theme-secondary:hover:not(.disabled){background:var(--secondary-variant);color:var(--secondary-variant-on)}:host .theme-highlight{background:var(--highlight-base);color:var(--highlight-on)}:host.clickable-card .theme-highlight:hover:not(.disabled){background:var(--highlight-variant);color:var(--highlight-variant-on)}:host .theme-danger{background:var(--danger-base);color:var(--danger-on)}:host.clickable-card .theme-danger:hover:not(.disabled){background:var(--danger-variant);color:var(--danger-variant-on)}:host .theme-warning{background:var(--warning-base);color:var(--warning-on)}:host.clickable-card .theme-warning:hover:not(.disabled){background:var(--warning-variant);color:var(--warning-variant-on)}:host .theme-info{background:var(--info-base);color:var(--info-on)}:host.clickable-card .theme-info:hover:not(.disabled){background:var(--info-variant);color:var(--info-variant-on)}:host .theme-success{background:var(--success-base);color:var(--success-on)}:host.clickable-card .theme-success:hover:not(.disabled){background:var(--success-variant);color:var(--success-variant-on)}:host .main-container{width:100%;height:100%;overflow:hidden;border-radius:3px;opacity:0;transition:opacity .5s linear}:host .main-container.show{opacity:1}:host .container{position:relative;width:100%;height:100%}:host .container .separator{height:0}:host .container .header{flex:2 1 auto}:host .container .body{display:flex;flex-direction:column;align-items:flex-start;justify-content:flex-end;flex:1 0 50%}:host .container .body .count-container{width:100%;height:100%;display:flex;justify-content:flex-end;align-items:center}:host .container .body .count-container .sub-count-container{display:flex;align-items:baseline;width:100%}:host .container .body .count-container .sub-count-container.hide{opacity:0}:host .container .body avn-icon{position:absolute;top:-25%;right:-25%;opacity:.2;z-index:0}:host .container .footer p{margin:var(--space-xs) 0 0 0}:host .container div{z-index:1;width:100%}:host .sub-container{min-height:inherit;display:flex;flex-direction:column;justify-content:space-between;height:100%;width:100%}:host.clickable-card{cursor:pointer}:host .svg-size{position:absolute;width:100%;top:-99999999999px;left:-99999999999px;opacity:0}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { mainContainer: [{
                type: ViewChild,
                args: ['mainContainer', { static: true }]
            }], svgSize: [{
                type: ViewChild,
                args: ['svgSize', { static: true }]
            }], title: [{
                type: Input
            }], theme: [{
                type: Input
            }], footerText: [{
                type: Input
            }], value: [{
                type: Input
            }], uom: [{
                type: Input
            }], footerTemplate: [{
                type: Input
            }], removeIcon: [{
                type: Input
            }], icon: [{
                type: Input
            }], valuePosition: [{
                type: Input
            }], valueSize: [{
                type: Input
            }], click: [{
                type: Output
            }], disabled: [{
                type: HostBinding,
                args: ['class.disabled']
            }, {
                type: Input
            }], addClickableClass: [{
                type: HostBinding,
                args: ['class.clickable-card']
            }] } });

class CountCardGridComponent {
    constructor(cdr) {
        this.cdr = cdr;
        /**
         * Define the width of the card
         */
        this.cardWidth = 200;
    }
    ngAfterContentInit() {
        this.colNumber = this.colMax;
    }
    calcColumnOnResize(event) {
        this.colNumber = this.colMax < Math.trunc(event.width / this.cardWidth) ? this.colMax : Math.trunc(event.width / this.cardWidth);
        this.cdr.detectChanges();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: CountCardGridComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.0.1", type: CountCardGridComponent, selector: "avn-count-card-grid", inputs: { cardWidth: "cardWidth", colMax: "colMax", gap: "gap" }, queries: [{ propertyName: "countCardComponent", predicate: CountCardComponent }], ngImport: i0, template: "<div\n  #countCardComponent\n  (avnResize)=\"calcColumnOnResize($event)\"\n  class=\"count-card-container\"\n  [style.grid-template-columns]=\"'repeat(' + (colMax < colNumber ? colMax : colNumber) + ', 1fr)'\"\n  [style.grid-auto-rows]=\"'minmax(' + cardWidth + 'px, auto)'\"\n  [style.gap]=\"gap\"\n>\n  <ng-content></ng-content>\n</div>\n", styles: [":host .count-card-container{display:grid;column-gap:20px;row-gap:20px;height:100%}\n"], dependencies: [{ kind: "directive", type: i2.ResizeDirective, selector: "[avnResize]", inputs: ["debounceTime", "emitInitialEvent"], outputs: ["avnResize"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: CountCardGridComponent, decorators: [{
            type: Component,
            args: [{ selector: 'avn-count-card-grid', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n  #countCardComponent\n  (avnResize)=\"calcColumnOnResize($event)\"\n  class=\"count-card-container\"\n  [style.grid-template-columns]=\"'repeat(' + (colMax < colNumber ? colMax : colNumber) + ', 1fr)'\"\n  [style.grid-auto-rows]=\"'minmax(' + cardWidth + 'px, auto)'\"\n  [style.gap]=\"gap\"\n>\n  <ng-content></ng-content>\n</div>\n", styles: [":host .count-card-container{display:grid;column-gap:20px;row-gap:20px;height:100%}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { countCardComponent: [{
                type: ContentChildren,
                args: [CountCardComponent]
            }], cardWidth: [{
                type: Input
            }], colMax: [{
                type: Input
            }], gap: [{
                type: Input
            }] } });

class CountCardModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: CountCardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.1", ngImport: i0, type: CountCardModule, declarations: [CountCardComponent, CountCardGridComponent], imports: [CommonModule, CoreModule, IconModule], exports: [CountCardComponent, CountCardGridComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: CountCardModule, providers: [PercentPipe, DatePipe], imports: [CommonModule, CoreModule, IconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: CountCardModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CountCardComponent, CountCardGridComponent],
                    imports: [CommonModule, CoreModule, IconModule],
                    exports: [CountCardComponent, CountCardGridComponent],
                    providers: [PercentPipe, DatePipe],
                }]
        }] });

const CONNECTOR_LINE_SETTINGS = new InjectionToken('CONNECTOR_LINE_SETTINGS');
const DEFAULT_CONNECTOR_LINE_SETTINGS = {
    lineOptions: {
        color: themeTypeToCssVar(ThemeType.PRIMARY, 'base'),
        size: 3,
        path: 'fluid',
        showEffectName: 'draw',
        animOptions: { duration: 300, timing: [0, 0, 0.2, 1] },
        outline: false,
        startPlug: 'behind',
        endPlug: 'arrow1',
        endPlugSize: 1,
        endPlugOutline: false,
    },
    labelOptions: {
        outlineColor: '',
        color: 'var(--main-on)',
    },
    hoverHighlightEnabled: true,
    highlightAnchorElements: true,
    hoverHighlightHiddenLineOpacity: 0.6,
    hoverHighlightHiddenAnchorOpacity: 0.6,
    triggerHighlightHiddenLineOpacity: 0,
    triggerHighlightHiddenAnchorOpacity: 0.2,
    showLabelOnPath: false,
};

/**
 * This service allows to display connector lines between 2 elements (also known as anchors)
 * Anchors can be placed anywhere in the DOM by applying the avnConnectorLine directive.
 * They can be added in separate components
 *
 */
class ConnectorLineService {
    constructor(settings, _document, _scrollDispatcher) {
        this.settings = settings;
        this._document = _document;
        this._scrollDispatcher = _scrollDispatcher;
        /** @ignore contains all the anchors that have been registered **/
        this._lineAnchors = [];
        /** @ignore contains all the active lines currently drawn **/
        this._activeLines = [];
        /** @ignore Emits when a line is destroyed **/
        this._lineDestroyedSubject = new Subject();
        /** @ignore Emits when a line needs to be repositioned **/
        this._repositionLineSubject = new Subject();
        /** @ignore Contains information about the currently highlighted lines **/
        this._connectorLinesHighlightInfoSubject = new BehaviorSubject({
            lineIds: [],
        });
        /** @ignore Keep track of the original anchor/parent element's dimensions rect prior to scaling.
         * This is required when the same element is transformed twice without being untransformed first. **/
        this._untransformedElementRects = [];
        /** @ignore When a resize occurs, the original anchor/parents element's dimensions may have changed.
         * This flag indicates if the dimensions of those elements should be recalculated **/
        this._shouldRecomputeElementRects = false;
        // disable the auto window resize listeners as we use resize observer for track this
        LeaderLine.positionByWindowResize = false;
        // watch for line resizing (execute the redraw on a animation frame)
        this._repositionLineSubject.pipe(observeOn(animationFrameScheduler)).subscribe((line) => {
            if (line === null) {
                Object.values(this._activeLines).forEach((activeLine) => activeLine.lineElement.position());
            }
            else {
                line.lineElement.position();
            }
        });
        this._resetHighlightOnOutsideClick();
    }
    /**
     * Emits every time the highlighted lines are changing
     * Contains information about the currently highlighted lines
     * **/
    get connectorLinesHighlightInfo$() {
        return this._connectorLinesHighlightInfoSubject.asObservable();
    }
    /**
     * Returns information about the currently highlighted lines
     * **/
    get connectorLinesHighlightInfo() {
        return this._connectorLinesHighlightInfoSubject.value;
    }
    /**
     * Adds a line anchor
     * **/
    addLineAnchor(anchor) {
        this._lineAnchors.push(anchor);
        // check if we can draw the line
        // we can have multiple line drawn from a single connector
        const startAnchor = anchor.type === 'start' ? anchor : this._lineAnchors.find((el) => el.id === anchor.id && el.type === 'start');
        const endAnchor = anchor.type === 'end' ? anchor : this._lineAnchors.find((el) => el.id === anchor.id && el.type === 'end');
        // draw the line once we have a start + end anchor only
        if (!startAnchor || !endAnchor) {
            return;
        }
        this.drawLine(startAnchor, endAnchor);
    }
    updateLineAnchor(id, type, updatedAnchor) {
        const anchorIndex = this._lineAnchors.findIndex((anchor) => anchor.id === id && anchor.type === type);
        if (anchorIndex !== -1) {
            this._lineAnchors[anchorIndex] = {
                id,
                type,
                ...updatedAnchor,
            };
        }
    }
    /**
     * Removes a line anchor
     * **/
    removeLineAnchor(id, type) {
        if (!id || !type) {
            return;
        }
        this._lineAnchors = this._lineAnchors.filter((el) => !(el.id === id && el.type === type));
        // remove the line connected to this anchor, if any
        const line = this._activeLines.find((l) => l.id === id);
        if (line) {
            // make sure to reset the line and anchor's visibility
            const isLineHidden = this._connectorLinesHighlightInfoSubject.getValue().lineIds.length > 0 &&
                !this._connectorLinesHighlightInfoSubject.getValue().lineIds.includes(line.id);
            if (isLineHidden) {
                this._toggleLineVisibility(line, true);
                this._untransformLineAnchors(line);
            }
            line.lineElement.remove();
            this._lineDestroyedSubject.next(line);
            // unobserve parent elements of this line (if not used by other lines)
            const allObservedElementsFromOtherLines = this._getResizeObservedElements(line);
            const elementsToUnobserve = line.observedElements.filter((el) => !allObservedElementsFromOtherLines.includes(el));
            elementsToUnobserve.forEach((el) => {
                this._resizeObserver.unobserve(el);
            });
            this._activeLines = this._activeLines.filter((l) => l.id !== id);
            this._connectorLinesHighlightInfoSubject.next({
                ...this.connectorLinesHighlightInfo,
                lineIds: this.connectorLinesHighlightInfo.lineIds.filter((lineId) => line.id !== lineId),
            });
        }
    }
    /**
     * Draws a line between two anchors
     * **/
    drawLine(anchorStart, anchorEnd) {
        if (!this._lineAnchors.includes(anchorStart)) {
            this._lineAnchors.push(anchorStart);
        }
        if (!this._lineAnchors.includes(anchorEnd)) {
            this._lineAnchors.push(anchorEnd);
        }
        const canDrawLine = anchorStart.id === anchorEnd.id && anchorStart.type !== anchorEnd.type;
        if (!canDrawLine) {
            console.warn('A line can only be drawn between two anchors with the same id and opposite type');
            return null;
        }
        // create the line with custom options (if any) passed via anchors
        const labels = this._toOptionLabels({ start: anchorStart.label, end: anchorEnd.label });
        const lineOptions = { ...this.settings.lineOptions, ...this._getLineOptions(anchorStart, anchorEnd), ...labels };
        const lineElement = new LeaderLine(anchorStart.elementRef, anchorEnd.elementRef, {
            ...lineOptions,
        });
        const lineDestroyed$ = this._lineDestroyedSubject.pipe(filter((destroyedLine) => destroyedLine.id === anchorStart.id));
        const startElementParents = this._getElementAncestors(anchorStart.elementRef);
        const endElementParents = this._getElementAncestors(anchorEnd.elementRef);
        const uniqueParents = [...startElementParents, ...endElementParents.filter((el) => !startElementParents.includes(el))];
        const currentlyObservedElements = this._getResizeObservedElements();
        const newElementsToObserve = uniqueParents.filter((el) => !currentlyObservedElements.includes(el));
        if (!this._resizeObserver && newElementsToObserve.length > 0) {
            this._resizeObserver = new ResizeObserver(() => {
                if (this.connectorLinesHighlightInfo.fromTrigger && this.connectorLinesHighlightInfo.lineIds.length > 0) {
                    this._shouldRecomputeElementRects = true;
                }
                this._repositionLineSubject.next(null);
            });
        }
        newElementsToObserve.forEach((el) => {
            this._resizeObserver.observe(el);
        });
        const svgElement = this._getLineSvg(lineElement._id);
        svgElement.style.transition = this._getAnimationTransition(['opacity'], lineOptions);
        // get the properties to animate
        // always animate transform in case 'scaling' is done via the trigger directive
        const transitionProperties = ['transform'];
        if (this.settings.highlightAnchorElements) {
            transitionProperties.push('opacity');
        }
        // only apply the animation to the anchor if no parent exist for the anchor
        const transitionElementStart = anchorStart.parentElementRef || anchorStart.elementRef;
        const transitionElementEnd = anchorEnd.parentElementRef || anchorEnd.elementRef;
        transitionElementStart.style.setProperty('transition', this._getAnimationTransition(transitionProperties, lineOptions));
        transitionElementEnd.style.setProperty('transition', this._getAnimationTransition(transitionProperties, lineOptions));
        // create the line
        const line = {
            id: anchorStart.id,
            lineElement,
            svgLineElement: svgElement,
            anchorStart,
            anchorEnd,
            observedElements: uniqueParents,
        };
        this._scrollDispatcher
            .ancestorScrolled(new ElementRef(anchorStart.elementRef), 0)
            .pipe(takeUntil(lineDestroyed$))
            .subscribe(() => {
            if (this.connectorLinesHighlightInfo.fromTrigger && this.connectorLinesHighlightInfo.lineIds.length > 0) {
                this._shouldRecomputeElementRects = true;
            }
            this._repositionLineSubject.next(line);
        });
        this._activeLines.push(line);
        return line;
    }
    /**
     * Removes a line by deleting both anchors
     * **/
    removeLine(id) {
        if (!id) {
            return;
        }
        this._lineAnchors = this._lineAnchors.filter((el) => el.id !== id);
        // remove all lines connected to this anchor
        const linesToRemove = this._activeLines.filter((line) => line.id === id);
        linesToRemove.forEach((line) => {
            line.lineElement.remove();
            this._lineDestroyedSubject.next(line);
            // unobserve parent elements of this line (if not used by other lines)
            const allObservedElementsFromOtherLines = this._getResizeObservedElements(line);
            const elementsToUnobserve = line.observedElements.filter((el) => !allObservedElementsFromOtherLines.includes(el));
            elementsToUnobserve.forEach((el) => {
                this._resizeObserver.unobserve(el);
            });
        });
        this._activeLines = this._activeLines.filter((line) => line.id !== id);
        this._connectorLinesHighlightInfoSubject.next({
            ...this.connectorLinesHighlightInfo,
            lineIds: this.connectorLinesHighlightInfo.lineIds.filter((lineId) => !linesToRemove.map((l) => l.id).includes(lineId)),
        });
    }
    /**
     * Clears all existing lines.
     * /!\ That will clear all existing and un-register all anchors (unless they are marked as persistent)
     * **/
    clearLines() {
        this._lineAnchors.filter((anchor) => !anchor.persistent).forEach((anchor) => this.removeLineAnchor(anchor.id, anchor.type));
        this._connectorLinesHighlightInfoSubject.next({ lineIds: [] });
    }
    /**
     * Highlights the lines connected to the specified elements
     *
     * @param elementRef The element for which lines should be highlighted
     * @param fromTrigger Indicates if the highlighting was initiated by a trigger element
     * @param scale When set, the anchor/parent element of the line anchors will be scaled by this amount
     */
    highlightElementLines(elementRef, fromTrigger = false, scale = null) {
        this._highlight(() => this._highlightElementLines(elementRef, fromTrigger, scale));
    }
    /**
     * Highlights the lines that belong to the specified group
     *
     * @param groups The name of the groups for which lines should be highlighted
     * @param fromTrigger Indicates if the highlighting was initiated by a trigger element
     * @param scale When set, the anchor/parent element of the line anchors will be scaled by this amount
     */
    highlightGroupLines(groups, fromTrigger = false, scale = null) {
        this._highlight(() => this._highlightGroupLines(groups, fromTrigger, scale));
    }
    /**
     * Clears the highlighted lines / group of lines
     */
    clearHighlightedLines() {
        this._activeLines.forEach((line) => this._toggleLineVisibility(line, true, this.connectorLinesHighlightInfo.fromTrigger));
        // reset anchors / parents size
        if (this.connectorLinesHighlightInfo.fromTrigger) {
            this._activeLines.forEach((line) => this._untransformLineAnchors(line));
            this._repositionLinesWhileAnimating();
        }
        this._shouldRecomputeElementRects = false;
        this._untransformedElementRects = [];
        this._connectorLinesHighlightInfoSubject.next({ lineIds: [] });
    }
    /**
     * Returns all the start/end connectors attached to the specified element
     *
     * @param elementRef the element for which to get the connector attached
     * @param withLineOnly set to true to return only connectors that have a line (i.e. there is a matching start/end connector)
     */
    getElementAnchors(elementRef, withLineOnly = false) {
        let anchors = this._lineAnchors.filter((anchor) => anchor.elementRef === elementRef || anchor.parentElementRef === elementRef);
        if (withLineOnly) {
            anchors = anchors.filter((anchor) => {
                const oppositeType = anchor.type === 'start' ? 'end' : 'start';
                return this._lineAnchors.some((c) => c.id === anchor.id && c.type === oppositeType);
            });
        }
        return anchors;
    }
    /**
     * Returns all the lines attached to the specified element
     *
     * @param elementRef the element for which to get lines attached
     * @param type set to 'start' to only retrieve the lines starting at this element. 'end' for the lines ending at this element. 'all' for both.
     */
    getElementLines(elementRef, type = 'all') {
        let elementAnchors = this.getElementAnchors(elementRef, true);
        if (type === 'start' || type === 'end') {
            elementAnchors = elementAnchors.filter((anchor) => anchor.type === type);
        }
        const lineIds = elementAnchors.map((anchor) => anchor.id);
        return this._activeLines.filter((line) => lineIds.includes(line.id));
    }
    /**
     * Returns all the lines that belong to the specified group
     */
    getGroupLines(group) {
        return this._activeLines.filter((line) => line.anchorStart.group === group || line.anchorEnd.group === group);
    }
    /**
     * Updates a label for the specified line
     */
    updateAnchorLabel(lineId, type, text) {
        const line = this._activeLines.find((activeLine) => activeLine.id === lineId);
        if (line) {
            const labels = this._toOptionLabels({ [type]: text });
            // update the options of the anchor + refresh the line
            this._lineAnchors.find((a) => a.id === lineId && a.type === type).label = text;
            line.lineElement.setOptions(labels);
        }
    }
    /**
     * Updates the options for the specified anchor
     */
    updateAnchorLineOptions(id, type, lineOptions) {
        // update the options of the anchor + refresh the line
        const anchor = this._lineAnchors.find((a) => a.id === id && a.type === type);
        if (!anchor) {
            return;
        }
        anchor.lineOptions = lineOptions;
        // update any existing line connected to this anchor
        const line = this._activeLines.find((activeLine) => activeLine.id === id);
        if (line) {
            line.lineElement.setOptions(this._getLineOptions(line.anchorStart, line.anchorEnd));
        }
    }
    /**
     * Toggles the visibility of the specified line
     */
    toggleLineVisibility(lineId, visible) {
        const line = this._activeLines.find((activeLine) => activeLine.id === lineId);
        if (line) {
            this._toggleLineVisibility(line, visible);
            const isCurrentlyHighlighted = this.connectorLinesHighlightInfo.lineIds.some((id) => id === lineId);
            if (!visible && isCurrentlyHighlighted) {
                this._connectorLinesHighlightInfoSubject.next({
                    ...this.connectorLinesHighlightInfo,
                    lineIds: this.connectorLinesHighlightInfo.lineIds.filter((id) => id !== lineId),
                });
            }
            else if (visible && !isCurrentlyHighlighted) {
                this._connectorLinesHighlightInfoSubject.next({
                    ...this.connectorLinesHighlightInfo,
                    lineIds: [...this.connectorLinesHighlightInfo.lineIds, lineId],
                });
            }
        }
    }
    /**
     * Refreshes the positions of the lines
     */
    updateLinesPosition() {
        this._repositionLineSubject.next(null);
    }
    /**
     * Refreshes the positions of a single line
     */
    updateLinePosition(lineId) {
        const line = this._activeLines.find((activeLine) => activeLine.id === lineId);
        if (line) {
            this._repositionLineSubject.next(line);
        }
    }
    /** @ignore Toggles the visibility of the specified line **/
    _toggleLineVisibility(line, visible, fromTrigger = false) {
        const highlightHiddenLineOpacity = fromTrigger ? this.settings.triggerHighlightHiddenLineOpacity : this.settings.hoverHighlightHiddenLineOpacity;
        const highlightHiddenAnchorOpacity = fromTrigger ? this.settings.triggerHighlightHiddenAnchorOpacity : this.settings.hoverHighlightHiddenAnchorOpacity;
        const useOpacity = highlightHiddenLineOpacity > 0;
        if (visible) {
            if (useOpacity) {
                line.svgLineElement.style.removeProperty('opacity');
            }
            else {
                // play with opacity to make the animation smoother
                line.svgLineElement.style.setProperty('opacity', `1`);
                line.lineElement.show(this.settings.lineOptions?.showEffectName || 'draw', this._getAnimationProperties(this._getLineOptions(line.anchorStart, line.anchorEnd)));
            }
            if (this.settings.highlightAnchorElements) {
                const transitionElementStart = line.anchorStart.parentElementRef || line.anchorStart.elementRef;
                const transitionElementEnd = line.anchorEnd.parentElementRef || line.anchorEnd.elementRef;
                transitionElementStart.style.removeProperty('opacity');
                transitionElementEnd.style.removeProperty('opacity');
            }
        }
        else {
            if (useOpacity) {
                line.svgLineElement.style.setProperty('opacity', `${highlightHiddenLineOpacity}`);
            }
            else {
                line.svgLineElement.style.setProperty('opacity', `0`);
                line.lineElement.hide(this.settings.lineOptions?.showEffectName || 'draw', this._getAnimationProperties(this._getLineOptions(line.anchorStart, line.anchorEnd)));
            }
            if (this.settings.highlightAnchorElements) {
                const transitionElementStart = line.anchorStart.parentElementRef || line.anchorStart.elementRef;
                const transitionElementEnd = line.anchorEnd.parentElementRef || line.anchorEnd.elementRef;
                transitionElementStart.style.setProperty('opacity', `${highlightHiddenAnchorOpacity || 0}`);
                transitionElementEnd.style.setProperty('opacity', `${highlightHiddenAnchorOpacity || 0}`);
            }
        }
    }
    /** @ignore Returns all elements currently observed for resize **/
    _getResizeObservedElements(excludeLine = null) {
        return this._activeLines
            .filter((line) => line !== excludeLine)
            .map((al) => al.observedElements)
            .reduce((res, elements) => [...res, ...elements.filter((el) => !res.includes(el))], []);
    }
    /** @ignore Returns the svg element associated with the specified line **/
    _getLineSvg(leaderLineId) {
        return Array.from(this._document.documentElement.querySelectorAll('.leader-line')).find((el) => !!el.querySelector(`#leader-line-${leaderLineId}-line-path`));
    }
    /** @ignore Calculates the animation transition to use based on settings **/
    _getAnimationTransition(properties, lineOptions) {
        const animOptions = this._getAnimationProperties(lineOptions);
        const timing = typeof animOptions.timing === 'string' ? `${animOptions.timing}` : `cubic-bezier(${animOptions.timing.join(',')})`;
        return properties.map((property) => `${property} ${animOptions.duration}ms ${timing}`).join(', ');
    }
    /** @ignore Calculates the animation properties to use based on settings **/
    _getAnimationProperties(lineOptions) {
        return {
            duration: 300,
            timing: [0, 0, 0.2, 1],
            ...(this.settings?.lineOptions?.animOptions || {}),
            ...(lineOptions?.animOptions || {}),
        };
    }
    /** @ignore Calculates the line options by combining options from the line anchors. Start anchor option will take precedence for duplicate options. **/
    _getLineOptions(anchorStart, anchorEnd) {
        return { ...(anchorEnd.lineOptions || {}), ...(anchorStart.lineOptions || {}) };
    }
    /** @ignore Convert labels to line options **/
    _toOptionLabels(labels) {
        const labelOptions = this.settings?.labelOptions || {};
        const res = {};
        if (Object.prototype.hasOwnProperty.call(labels, 'start')) {
            res['startLabel'] = !labels.start
                ? null
                : this.settings.showLabelOnPath
                    ? LeaderLine.pathLabel(labels.start, labelOptions)
                    : LeaderLine.captionLabel(labels.start, labelOptions);
        }
        if (Object.prototype.hasOwnProperty.call(labels, 'end')) {
            res['endLabel'] = !labels.end
                ? null
                : this.settings.showLabelOnPath
                    ? LeaderLine.pathLabel(labels.end, labelOptions)
                    : LeaderLine.captionLabel(labels.end, labelOptions);
        }
        return res;
    }
    /**
     * @ignore Returns all the parent element of the specified element
     * **/
    _getElementAncestors(element) {
        const res = [];
        if (!element?.parentElement) {
            return res;
        }
        return [...res, element.parentElement, ...(this._getElementAncestors(element.parentElement) || [])];
    }
    /** @ignore */
    _highlight(highlightFn) {
        // check if we need to recalculate the untransformed/original elements dimension rectangles
        if (this._shouldRecomputeElementRects && this._untransformedElementRects.length > 0) {
            // if that's the case, wait for the highlighted lines to be cleared first to reset the highlighted element's dimensions so we can recompute them
            // a bit hackish but no better solution for now
            this._untransformedElementRects = [];
            this._shouldRecomputeElementRects = false;
            this.clearHighlightedLines();
            timer(this.settings.lineOptions.animOptions.duration * 2).subscribe(() => highlightFn());
        }
        else {
            highlightFn();
        }
    }
    /** @ignore */
    _highlightElementLines(elementRef, fromTrigger = false, scale = null) {
        const elementAnchors = this.getElementAnchors(elementRef, true);
        // toggle all lines connected to the specified element
        const elementLines = elementAnchors
            .map((anchor) => this._activeLines.filter((line) => line.anchorStart === anchor || line.anchorEnd === anchor))
            .reduce((p, c) => [...p, ...c], []);
        this._activeLines.forEach((line) => this._untransformLineAnchors(line));
        this._activeLines.filter((line) => !elementLines.includes(line)).forEach((line) => this._toggleLineVisibility(line, false, fromTrigger));
        elementLines.forEach((line) => this._toggleLineVisibility(line, true, fromTrigger));
        // also make the anchors / parents bigger if configured
        if (fromTrigger && scale) {
            this._transformLinesAnchors(elementLines, scale);
            this._repositionLinesWhileAnimating();
        }
        this._connectorLinesHighlightInfoSubject.next({ lineIds: elementLines.map((line) => line.id), elementRef, fromTrigger });
    }
    /** @ignore */
    _highlightGroupLines(groups, fromTrigger = false, scale = null) {
        // get all lines in group
        const groupLines = this._activeLines.filter((line) => groups.includes(line.anchorStart.group) || groups.includes(line.anchorEnd.group));
        // untransform any previous active line
        this._activeLines.forEach((line) => this._untransformLineAnchors(line));
        // hide all lines not belonging to the group to highlight
        this._activeLines.filter((line) => !groupLines.includes(line)).forEach((line) => this._toggleLineVisibility(line, false, fromTrigger));
        // highlight all lines belonging to the group to highlight
        groupLines.forEach((line) => this._toggleLineVisibility(line, true, fromTrigger));
        // transform the anchor elements if a scale was passed and the highlight originates from a trigger
        if (fromTrigger && scale) {
            this._transformLinesAnchors(groupLines, scale);
            // we need to keep repositioning the lines during the animation to avoid the flicker
            this._repositionLinesWhileAnimating();
        }
        this._connectorLinesHighlightInfoSubject.next({ lineIds: groupLines.map((line) => line.id), groups, fromTrigger });
    }
    /** @ignore Applies scale transformation all the anchors of the specified lines */
    _transformLinesAnchors(lines, scale) {
        const elementsToTransform = [];
        const zIndexMap = new Map();
        lines.forEach((line) => {
            const transformElementStart = line.anchorStart.parentElementRef || line.anchorStart.elementRef;
            const transformElementEnd = line.anchorEnd.parentElementRef || line.anchorEnd.elementRef;
            if (!elementsToTransform.includes(transformElementStart)) {
                elementsToTransform.push(transformElementStart);
                zIndexMap.set(transformElementStart, line.anchorStart.lineOptions?.zIndex);
            }
            if (!elementsToTransform.includes(transformElementEnd)) {
                elementsToTransform.push(transformElementEnd);
                zIndexMap.set(transformElementEnd, line.anchorEnd.lineOptions?.zIndex);
            }
            // also add the z-index to the lines so they dont appear behind the scaled elements
            line.svgLineElement.style.setProperty('z-index', `${line.anchorStart.lineOptions?.zIndex}`);
        });
        const boundingRects = elementsToTransform.map((element) => ({ element, rect: element.getBoundingClientRect() }));
        // keep original rects (non transformed) of elements already transformed
        this._untransformedElementRects = this._untransformedElementRects.filter((el) => boundingRects.map((br) => br.element).includes(el.element));
        const translations = this._getElementTranslations(scale, boundingRects);
        elementsToTransform.forEach((element) => {
            const translateXValue = translations.find((e) => element === e.element).translateX || 0;
            const translateYValue = translations.find((e) => element === e.element).translateY || 0;
            element.style.setProperty('transform', `scale(${scale}) translate(${translateXValue}px, ${translateYValue}px)`);
            element.style.setProperty('z-index', `${zIndexMap.get(element)}`);
            // keep track of our original element dimensions, in case its transformed again before being untransformed (yeah, lots of fun :D)
            if (!this._untransformedElementRects.some((er) => er.element === element)) {
                this._untransformedElementRects.push(boundingRects.find((br) => br.element === element));
            }
        });
    }
    /** @ignore Calculates the amount of translation required so the specified elements dont collide */
    _getElementTranslations(scale, elementsRects) {
        const res = [...elementsRects];
        // X axis - sort elements by x position
        const sortedByXPos = elementsRects.sort((a, b) => a.rect.x - b.rect.x);
        sortedByXPos.map((elementRect, i) => {
            // for each element, find the nearest element intersecting on the x axis
            const nearestElementOnXAxis = this._findNearestElementOnXAxis(elementRect, elementsRects.slice(i + 1));
            if (nearestElementOnXAxis) {
                const elementDomRect = this._getUntransformedBoundingRect(elementRect.element);
                const nearestElementOnXAxisDomRect = this._getUntransformedBoundingRect(nearestElementOnXAxis.element);
                // check if there will be enough space (at least 10px) between those elements after scaling
                const spaceBetweenPx = nearestElementOnXAxisDomRect.x - (elementDomRect.x + elementDomRect.width);
                const spaceBetweenPxAfterScale = spaceBetweenPx -
                    (elementDomRect.width * scale - elementDomRect.width) / 2 -
                    (nearestElementOnXAxisDomRect.width * scale - nearestElementOnXAxisDomRect.width) / 2;
                if (spaceBetweenPxAfterScale < 10) {
                    const translateX = -(Math.abs(spaceBetweenPxAfterScale) + 10);
                    res.find((r) => r.element === elementRect.element).translateX = translateX;
                }
            }
        });
        // Y axis - sort elements by y position
        const sortedByYPos = elementsRects.sort((a, b) => a.rect.y - b.rect.y);
        sortedByYPos.map((elementRect, i) => {
            const nearestElementOnYAxis = this._findNearestElementOnYAxis(elementRect, elementsRects.slice(i + 1), res);
            if (nearestElementOnYAxis) {
                const elementDomRect = this._getUntransformedBoundingRect(elementRect.element);
                const nearestElementOnYAxisDomRect = this._getUntransformedBoundingRect(nearestElementOnYAxis.element);
                // check if there will be enough space (at least 10px) between those elements after scaling
                const spaceBetweenPx = nearestElementOnYAxisDomRect.y - (elementDomRect.y + elementDomRect.height);
                const spaceBetweenPxAfterScale = spaceBetweenPx -
                    (elementDomRect.height * scale - elementDomRect.height) / 2 -
                    (nearestElementOnYAxisDomRect.height * scale - nearestElementOnYAxisDomRect.height) / 2;
                if (spaceBetweenPxAfterScale < 10) {
                    const translateY = -(Math.abs(spaceBetweenPxAfterScale) + 10);
                    res.find((r) => r.element === elementRect.element).translateY = translateY;
                }
            }
        });
        return res;
    }
    /** @ignore Find the nearest element that intersects with the specified element on the X axis */
    _findNearestElementOnXAxis(elementRect, otherElementRects) {
        const elementDomRect = this._getUntransformedBoundingRect(elementRect.element);
        const elementTop = elementDomRect.y;
        const elementBottom = elementTop + elementDomRect.height;
        return otherElementRects.find((otherElementRect) => {
            const otherElementDomRect = this._getUntransformedBoundingRect(otherElementRect.element);
            const otherElementTop = otherElementDomRect.y;
            const otherElementBottom = otherElementTop + otherElementDomRect.height;
            const intersectsOnXAxis = (elementTop >= otherElementTop && elementTop <= otherElementBottom) || (elementBottom >= otherElementTop && elementBottom <= otherElementBottom);
            return intersectsOnXAxis;
        });
    }
    /** @ignore Find the nearest element that intersects with the specified element on the Y axis */
    _findNearestElementOnYAxis(elementRect, otherElementRects, xTranslations) {
        // retrieve any translation already made on the x axis
        const elementXAxisTranslation = xTranslations.find((v) => v.element === elementRect.element)?.translateX || 0;
        const elementDomRect = this._getUntransformedBoundingRect(elementRect.element);
        const elementLeft = elementDomRect.x + elementXAxisTranslation;
        const elementRight = elementLeft + elementDomRect.width;
        return otherElementRects.find((otherElementRect) => {
            // retrieve any translation already made on the x axis
            const otherElementXAxisTranslation = xTranslations.find((v) => v.element === otherElementRect.element)?.translateX || 0;
            const otherElementDomRect = this._getUntransformedBoundingRect(otherElementRect.element);
            const otherElementLeft = otherElementDomRect.x + otherElementXAxisTranslation;
            const otherElementRight = otherElementLeft + otherElementDomRect.width;
            const intersectsOnYAxis = (elementLeft >= otherElementLeft && elementLeft <= otherElementRight) || (elementRight >= otherElementLeft && elementRight <= otherElementRight);
            return intersectsOnYAxis;
        });
    }
    /** @ignore returns the original dimensions rectangle of the element (if currently scaled) or its current dimensions **/
    _getUntransformedBoundingRect(element) {
        return this._untransformedElementRects.find((ue) => element === ue.element)?.rect || element.getBoundingClientRect();
    }
    /** @ignore remove transformations applied to all anchors of the specified lines **/
    _untransformLineAnchors(line) {
        const transitionElementStart = line.anchorStart.parentElementRef || line.anchorStart.elementRef;
        const transitionElementEnd = line.anchorEnd.parentElementRef || line.anchorEnd.elementRef;
        transitionElementStart.style.removeProperty('transform');
        transitionElementStart.style.removeProperty('z-index');
        transitionElementEnd.style.removeProperty('transform');
        transitionElementEnd.style.removeProperty('z-index');
        line.svgLineElement.style.removeProperty('z-index');
    }
    /** @ignore reposition the lines periodically for the time of the animation **/
    _repositionLinesWhileAnimating() {
        timer(0, 10)
            .pipe(takeUntil(timer(this.settings.lineOptions.animOptions.duration)), endWith(0))
            .subscribe(() => {
            this.updateLinesPosition();
        });
    }
    /** @ignore Listens for click made outside of highlighted anchors, to clear the highlighting **/
    _resetHighlightOnOutsideClick() {
        fromEvent(this._document, 'mousedown')
            .pipe(filter(() => this.connectorLinesHighlightInfo.fromTrigger && this.connectorLinesHighlightInfo.lineIds.length > 0))
            .subscribe((evt) => {
            const clickTarget = evt.target;
            const highlightedLines = this.connectorLinesHighlightInfo.lineIds.map((lineId) => this._activeLines.find((line) => line.id === lineId));
            const highlightedElementClicked = highlightedLines.some((line) => this._isOrContainsElement(line, clickTarget));
            if (!highlightedElementClicked) {
                const otherElementClicked = this._activeLines.some((line) => this._isOrContainsElement(line, clickTarget));
                if (!otherElementClicked) {
                    this.clearHighlightedLines();
                }
            }
        });
    }
    /** @ignore indicates if the specified element is one of the line anchors/parents or one of their children **/
    _isOrContainsElement(line, element) {
        return !!(line.anchorStart.elementRef === element ||
            line.anchorStart.elementRef.contains(element) ||
            line.anchorStart.parentElementRef === element ||
            line.anchorStart.parentElementRef?.contains(element) ||
            line.anchorEnd.elementRef === element ||
            line.anchorEnd.elementRef.contains(element) ||
            line.anchorEnd.parentElementRef === element ||
            line.anchorEnd.parentElementRef?.contains(element));
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ConnectorLineService, deps: [{ token: CONNECTOR_LINE_SETTINGS }, { token: DOCUMENT }, { token: i1$1.ScrollDispatcher }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ConnectorLineService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ConnectorLineService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CONNECTOR_LINE_SETTINGS]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i1$1.ScrollDispatcher }]; } });

/**
 * Directive that facilitates creating anchors of connector lines
 * **/
class ConnectorLineAnchorDirective {
    /** @ignore */
    constructor(_elementRef, connectorLineService) {
        this._elementRef = _elementRef;
        this.connectorLineService = connectorLineService;
        /** The anchor info and options */
        this.anchors = [];
        /** Indicates if the anchor is persistent.
         * When not marked as persistent, the anchor will be unregisterd once a line attached to it is destroyed.
         * When marked as persistent, the anchor will still be registerd once a line attached to it is destroyed.
         * */
        this.persistent = false;
        /** @ignore Indicates if the current element has anchors attached to it */
        this._hasAnchorSubject = new Subject();
        /** @ignore */
        this._componentDestroyed$ = new Subject();
    }
    ngOnChanges({ anchors }) {
        if (anchors && !anchors.isFirstChange()) {
            const previous = anchors?.previousValue || [];
            const current = anchors?.currentValue || [];
            const added = current.filter((c) => !previous.some((p) => p.id === c.id && p.type === c.type));
            const removed = previous.filter((p) => !current.some((c) => c.id === p.id && c.type === p.type));
            const existing = current.filter((c) => !added.includes(c));
            removed.forEach((id) => {
                this.connectorLineService.removeLineAnchor(id.id, id.type);
            });
            added.forEach((anchor) => {
                this.connectorLineService.addLineAnchor({
                    ...anchor,
                    elementRef: this._elementRef.nativeElement,
                    parentElementRef: this.parentElement,
                    persistent: !!this.persistent,
                });
            });
            const changedLabels = existing
                .map((c) => ({ id: c.id, type: c.type, current: c.label, previous: previous.find((p) => p.id === c.id).label }))
                .filter((c) => c.previous !== c.current);
            changedLabels.forEach((c) => {
                this.connectorLineService.updateAnchorLabel(c.id, c.type, c.current);
            });
            const changedLineOptions = existing
                .map((c) => ({ id: c.id, type: c.type, current: c.lineOptions, previous: previous.find((p) => p.id === c.id).lineOptions }))
                .filter((c) => !isEqual(c.previous, c.current));
            changedLineOptions.forEach((c) => {
                this.connectorLineService.updateAnchorLineOptions(c.id, c.type, c.current);
            });
            this._hasAnchorSubject.next(current.length > 0);
            if (this.connectorLineService.settings.hoverHighlightEnabled && previous.length === 0 && current.length > 0) {
                this._addHoverListener();
            }
        }
    }
    /** @ignore **/
    ngAfterViewInit() {
        this.anchors?.forEach((anchor) => {
            this.connectorLineService.addLineAnchor({
                ...anchor,
                elementRef: this._elementRef.nativeElement,
                parentElementRef: this.parentElement,
                persistent: !!this.persistent,
            });
        });
        if (this.connectorLineService.settings.hoverHighlightEnabled && this.anchors?.length > 0) {
            this._addHoverListener();
        }
    }
    /** @ignore Adds mouse listeners for hover on the anchor to highlight the element lines **/
    _addHoverListener() {
        const hasNoAnchors$ = this._hasAnchorSubject.asObservable().pipe(filter((hasAnchors) => !hasAnchors));
        const element = this.parentElement || this._elementRef.nativeElement;
        fromEvent(element, 'mouseenter')
            .pipe(withLatestFrom(this.connectorLineService.connectorLinesHighlightInfo$), filter(([_, highlightedLines]) => !highlightedLines.fromTrigger), takeUntil(merge(hasNoAnchors$, this._componentDestroyed$)))
            .subscribe(() => this.connectorLineService.highlightElementLines(element, false));
        fromEvent(element, 'mouseleave')
            .pipe(withLatestFrom(this.connectorLineService.connectorLinesHighlightInfo$), filter(([_, highlightedLines]) => !highlightedLines.fromTrigger), takeUntil(merge(hasNoAnchors$, this._componentDestroyed$)))
            .subscribe(() => {
            this.connectorLineService.clearHighlightedLines();
        });
    }
    /** @ignore **/
    ngOnDestroy() {
        this._componentDestroyed$.next();
        this._componentDestroyed$.complete();
        this.anchors?.forEach((id) => {
            this.connectorLineService.removeLineAnchor(id.id, id.type);
        });
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ConnectorLineAnchorDirective, deps: [{ token: i0.ElementRef }, { token: ConnectorLineService }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.1", type: ConnectorLineAnchorDirective, selector: "[avnConnectorLineAnchor]", inputs: { anchors: ["avnConnectorLineAnchor", "anchors"], persistent: ["avnConnectorLineAnchorPersistent", "persistent"], parentElement: ["avnConnectorLineAnchorParentElement", "parentElement"] }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ConnectorLineAnchorDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[avnConnectorLineAnchor]' }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: ConnectorLineService }]; }, propDecorators: { anchors: [{
                type: Input,
                args: ['avnConnectorLineAnchor']
            }], persistent: [{
                type: Input,
                args: ['avnConnectorLineAnchorPersistent']
            }], parentElement: [{
                type: Input,
                args: ['avnConnectorLineAnchorParentElement']
            }] } });

class ConnectorLineTriggerDirective {
    /** @ignore */
    constructor(_elementRef, _document, connectorLineService) {
        this._elementRef = _elementRef;
        this._document = _document;
        this.connectorLineService = connectorLineService;
        /** The type of event that will trigger the highlight of the lines **/
        // eslint-disable-next-line @angular-eslint/no-input-rename
        this.triggerType = 'click';
        this._groupChanged = new Subject();
        /** @ignore */
        this._componentDestroyed$ = new Subject();
    }
    ngOnInit() {
        if (!this.group) {
            this._watchForHighlight(this.triggerType, () => {
                this.connectorLineService.highlightElementLines(this._elementRef.nativeElement, true, this.scale);
            });
        }
    }
    ngOnChanges({ group, trigger, disabled }) {
        if (group && !!this.triggerType) {
            this._groupChanged.next();
            this._watchForHighlight(this.triggerType, () => {
                this.connectorLineService.highlightGroupLines([this.group], true, this.scale);
            });
        }
        if (trigger?.currentValue === 'click' && !this.disabled) {
            this._elementRef.nativeElement.style.cursor = 'pointer';
        }
        if (disabled && !disabled.isFirstChange() && !!disabled.currentValue && this.triggerType === 'click') {
            this._elementRef.nativeElement.style.cursor = 'unset';
        }
    }
    /** @ignore **/
    ngOnDestroy() {
        this._componentDestroyed$.next();
        this._componentDestroyed$.complete();
    }
    _watchForHighlight(trigger, highlightFn) {
        const events$ = this._getEventObservables(trigger);
        events$.start
            .pipe(filter(() => !this.disabled), takeUntil(merge(this._groupChanged, this._componentDestroyed$)))
            .subscribe(() => {
            highlightFn();
        });
        events$.end
            .pipe(filter(() => !this.disabled), takeUntil(merge(this._groupChanged, this._componentDestroyed$)))
            .subscribe(() => {
            this.connectorLineService.clearHighlightedLines();
        });
    }
    _getEventObservables(trigger) {
        if (trigger === 'hover') {
            return {
                start: fromEvent(this._elementRef.nativeElement, 'mouseenter'),
                end: fromEvent(this._elementRef.nativeElement, 'mouseleave'),
            };
        }
        else if (trigger === 'click') {
            // for clicks trigger, every alternate click should toggle the visibility of the lines
            // however if other lines gets highlighted via a trigger between two clicks, that behaviour should be reset (i.e. second click should show the lines again)
            const highlightedLinesChanged$ = this.connectorLineService.connectorLinesHighlightInfo$.pipe(skip(1), filter((evt) => !evt.groups?.includes(this.group) && evt.elementRef !== this._elementRef.nativeElement && evt.fromTrigger));
            return {
                start: fromEvent(this._elementRef.nativeElement, 'click').pipe(startWith(null), bufferCount(2), takeUntil(highlightedLinesChanged$)).pipe(repeat()),
                end: fromEvent(this._elementRef.nativeElement, 'click').pipe(bufferCount(2), takeUntil(highlightedLinesChanged$)).pipe(repeat()),
            };
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ConnectorLineTriggerDirective, deps: [{ token: i0.ElementRef }, { token: DOCUMENT }, { token: ConnectorLineService }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.1", type: ConnectorLineTriggerDirective, selector: "[avnConnectorLineTrigger]", inputs: { group: ["avnConnectorLineTrigger", "group"], triggerType: ["avnConnectorLineTriggerType", "triggerType"], disabled: ["avnConnectorLineTriggerDisabled", "disabled"], scale: ["avnConnectorLineTriggerScale", "scale"] }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ConnectorLineTriggerDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[avnConnectorLineTrigger]' }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: ConnectorLineService }]; }, propDecorators: { group: [{
                type: Input,
                args: ['avnConnectorLineTrigger']
            }], triggerType: [{
                type: Input,
                args: ['avnConnectorLineTriggerType']
            }], disabled: [{
                type: Input,
                args: ['avnConnectorLineTriggerDisabled']
            }], scale: [{
                type: Input,
                args: ['avnConnectorLineTriggerScale']
            }] } });

class ConnectorLineModule {
    static forRoot(config) {
        return {
            ngModule: ConnectorLineModule,
            providers: [
                {
                    provide: CONNECTOR_LINE_SETTINGS,
                    useValue: {
                        ...DEFAULT_CONNECTOR_LINE_SETTINGS,
                        ...(config || {}),
                        lineOptions: { ...DEFAULT_CONNECTOR_LINE_SETTINGS.lineOptions, ...(config?.lineOptions || {}) },
                        labelOptions: { ...DEFAULT_CONNECTOR_LINE_SETTINGS.labelOptions, ...(config?.labelOptions || {}) },
                    },
                },
            ],
        };
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ConnectorLineModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.1", ngImport: i0, type: ConnectorLineModule, declarations: [ConnectorLineAnchorDirective, ConnectorLineTriggerDirective], imports: [CommonModule], exports: [ConnectorLineAnchorDirective, ConnectorLineTriggerDirective] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ConnectorLineModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.1", ngImport: i0, type: ConnectorLineModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [ConnectorLineAnchorDirective, ConnectorLineTriggerDirective],
                    imports: [CommonModule],
                    exports: [ConnectorLineAnchorDirective, ConnectorLineTriggerDirective],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AreaRangeSeriesDirective, CONNECTOR_LINE_SETTINGS, CartesianSeries, CartesianSeriesDirective, ChartCoreModule, ChartLegendDirective, ChartLibService, ChartThemeAdapterService, ChartTooltipDirective, ChartYAxisDirective, ConnectorLineAnchorDirective, ConnectorLineModule, ConnectorLineService, ConnectorLineTriggerDirective, CountCardComponent, CountCardGridComponent, CountCardModule, CountCardValueSize, DEFAULT_CONNECTOR_LINE_SETTINGS, DEFAULT_DATAVIZ_Y_AXIS, DonutComponent, DonutModule, HIGHCHARTS_MODULES, LegendFormatter, LineSeriesDirective, PercentDonutComponent, PercentDonutModule, PieChart, PieComponent, PieModule, SeriesTypes, SimpleChartTooltipFormatterDirective, SimpleLegendDirective, SimpleYAxisDirective, SliceChartLegendDirective, SliceChartTooltipFormatterDirective, SolidGaugeComponent, SolidGaugeModule, SplineSeriesDirective, StepLineSeriesDirective, SunburstComponent, SunburstModule, TimeseriesChartTooltipFormatterDirective, TimeseriesComponent, TimeseriesLegendDirective, TimeseriesModule, TooltipFormatter, YAxisPropertiesBase, _CartesianSeriesBase, _ChartLegendBase, _ChartTooltipBase, _ChartYAxisBase, hcModuleFactory, mixinCartesianSeries, mixinChartBase, mixinLegendFormatter, mixinTooltipFormatter, mixinYAxisFeatures, mixinYAxisProperties, themeTypeToCssVar };
